import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  en: {
    translation: {
      header: {
        welcome: "Welcome to",
        tagline: "Helping families flourish.",
        cta: "Schedule an Appointment",
      },
      landing: {
        servicesTitle: "Our Services",
        servicesDescription:
          "GeneFlou is a genetic counselling service whose mission is to help growing families navigate their genetic testing options and understand their reproductive risks. We strive to fill the service gap in Chinese communities by providing our services in Cantonese, Mandarin, and English.",
        haveQuestions: "Have Questions?",
        contactPrompt: "Contact us to learn more about our services and how we can help you.",
        getInTouch: "Get in Touch",
        serviceSection: {
          overview: { title: "Overview" },
          whatToExpect: { title: "What to Expect" },
          process: { title: "The Process" },
          faq: { title: "Frequently Asked Questions" },
          onThisPage: "On this page",
        },

        service: {
          geneticCounselling: {
            title: "Genetic Counselling",
            body:
              "Genetic counselling is the process of helping people understand and adapt to the medical, psychological, and family implications of genetic contributions to health and disease. At GeneFlou, we provide information and support so individuals and families can make informed decisions about genetic testing and their health.",
            details:
              "Genetic counsellors are healthcare professionals with specialized education in medical genetics and counselling. They review personal and family health history, help people understand genetic risks, explain what genetic testing can and cannot tell them, and discuss the medical and emotional implications of test results.\n\nGeneFlou provides personalized consultations in English, Mandarin, and Cantonese, with a focus on reproductive health, prenatal screening, and hereditary conditions. Our goal is to give you clear information and supportive, non-directive counselling so your decisions reflect your needs, values, and family circumstances.",
            whenToReachOut: "When to Reach Out to Us",
            whenToReachOutSubtitle: "A consultation with GeneFlou may be particularly helpful if you:",
            whenToReachOutBulletsArray: [
              "Have received a genetic screening report and need help interpreting the results.",
              "Suspect a hereditary condition or have concerns regarding your family health history.",
              "Are considering preconception or prenatal testing and want to understand your options.",
              "Require specialized care and guidance provided fluently in Mandarin or Cantonese.",
              "Are a proactive planner who believes in expecting the unexpected and wishes to gain a clear, comprehensive understanding of your genetic health before starting the next chapter of your life.",
            ],
            whatToExpect: {
              body:
                "Your consultation is designed to be a supportive, judgment-free conversation tailored to your unique situation and concerns.\n\nWhat happens during a session\n• A thorough review of your personal and family medical history\n• A clear explanation of genetic concepts in plain, accessible language\n• A discussion of available genetic testing options and what each can and cannot tell you\n• Emotional support and guidance as you consider your options\n• A personalized summary with recommendations and next steps\n\nSessions are typically 30–60 minutes and are conducted virtually, in English, Mandarin, or Cantonese. You are welcome to have a partner or family member join you. No question is too small — we encourage you to come with anything on your mind.",
            },
            process: {
              body:
                "Step 1: Reach Out\nContact us at genetics@geneflou.com or through our website to express your interest. We will respond to schedule your consultation and gather any preliminary information you can share.\n\nStep 2: Pre-Consultation Intake\nBefore your appointment, we will send you a brief intake form to collect your personal and family medical history. This allows us to prepare and make the most of your session.\n\nStep 3: Consultation\nDuring your session, your genetic counsellor will review your history, discuss your concerns, explain relevant genetic concepts, and outline your testing options and reproductive risks. This is a two-way conversation — your values and preferences guide the discussion.\n\nStep 4: Testing (if applicable)\nIf you decide to proceed with genetic testing, we will explain how it is arranged where you live and point you to the provider who can order it, such as your family doctor or a specialist. We will help you understand what is being tested and the expected timeline for results.\n\nStep 5: Results & Follow-Up\nOnce results are available, your genetic counsellor will walk you through what they mean in clear, compassionate language. We discuss any implications for you and your family, explore your options, and support you in making informed decisions about next steps. You also receive a detailed written summary of your consultation and its recommendations. It is yours to keep and to share as you choose, whether with relatives for whom the findings may also be relevant or with your own healthcare providers.",
            },
            faq: {
              items: [
                {
                  q: "What is a genetic counsellor?",
                  a:
                    "A genetic counsellor is a healthcare professional with specialized training in medical genetics and counselling. Genetic counsellors review personal and family health history, explain genetic risks and testing options, interpret results, and provide support as people make informed decisions. GeneFlou's genetic counselling is provided by a Canadian-certified genetic counsellor.",
                },
                {
                  q: "Do I need a referral to book a consultation?",
                  a:
                    "No referral is required. You can contact us directly at genetics@geneflou.com to schedule an appointment. However, if you have previous genetic test results or medical records, we may ask you to share them beforehand so we can provide the most relevant guidance.",
                },
                {
                  q: "What languages are consultations available in?",
                  a:
                    "We offer consultations in English, Mandarin, and Cantonese. We specifically founded GeneFlou to help fill the service gap in Chinese-speaking communities where genetic counselling is not always offered as part of standardized care.",
                },
                {
                  q: "Is the consultation confidential, and where is my information stored?",
                  a:
                    "Absolutely. All consultations are conducted in strict confidence, in accordance with professional and legal privacy standards, and your information is never shared without your explicit consent. Your intake form, consultation notes, and any reports you share with us are stored in Canada.",
                },
                {
                  q: "Can a genetic counsellor order genetic tests for me?",
                  a:
                    "Not at present. GeneFlou does not order genetic testing. What your genetic counsellor can do is provide you with information about what tests are available to you, given your personal and family history, and guide you through the process, explaining how testing is arranged where you live and pointing you to the provider who can order it, usually your family doctor or a specialist. We then help you understand what is being tested and what the results mean.",
                },
                {
                  q: "What if I already have genetic test results?",
                  a:
                    "We frequently work with individuals who have already received genetic screening reports and need help interpreting them. Send your results to us ahead of your consultation and we will break down what they mean, discuss any implications, and help you plan your next steps.",
                },
              ],
            },
          },
          reproductiveRisk: {
            title: "Reproductive Risk Assessment",
            body:
              "We review your personal and family health history to identify patterns that may suggest inherited reproductive risks, and where it can be calculated, we work out the chance of a condition being passed on. We explain the limits of a family-history assessment, whether you meet the criteria for publicly funded testing where you live, and whether carrier screening, targeted testing, prenatal screening, diagnostic testing, or another specialist assessment may tell you more.",
            details:
              "Personal and family health history is an important part of genetic risk assessment. A genetic counsellor can review health conditions on both sides of the family, ages at diagnosis, ancestry, pregnancy history, and known genetic test results. This may reveal patterns that suggest an inherited risk, but it does not provide a complete picture of your DNA or identify every genetic condition.\n\nPedigree Analysis\nWhen helpful, we organize the available information into a multi-generational family tree called a pedigree. A pedigree can help explain possible inheritance patterns and show where more focused assessment or testing may be useful.\n\nWho May Benefit?\nThis service may be helpful for:\n• Individuals or couples with a known or suspected genetic condition in the family.\n• People with a family history of birth differences, intellectual disability, early-onset disease, or repeated cases of the same condition.\n• People who have previous genetic test results and want help understanding what they may mean for a pregnancy.\n• People planning a pregnancy who want to discuss carrier screening or prenatal testing.\n\nPossible Next Steps\nDepending on the findings, next steps may include no additional testing, targeted testing for a known familial variant, carrier screening, prenatal screening or diagnostic testing, or referral to another specialist. Recommendations are personalized, and no family-history assessment can provide absolute certainty.",
            diagram: {
              title: "How to read a pedigree",
              intro:
                "A pedigree is a family tree drawn with a standard set of symbols, so that any genetics professional can read it the same way. Here is an example of the kind of chart we build with you, and what each mark means.",
              legend: {
                male: "Male",
                female: "Female",
                carrier: "Carrier",
                affected: "Has the condition",
                unknown: "Status not known",
                consultand: "The person the assessment is about",
              },
              reading:
                "Reading this example: a grandparent in generation I is a carrier. In generation II, one of their children has the condition and another is a carrier. In generation III, the person seeking the assessment has not been tested, so their own carrier status is not yet known — and neither is their sibling's, marked with a question mark.",
              footnote:
                "These are the conventional symbols used in medical pedigrees. A real pedigree also records ages, ages at diagnosis, ancestry, and pregnancy history, and it is only as complete as the family information available.",
              description:
                "An example three-generation pedigree. Squares represent males and circles represent females; a solid symbol means the person has the condition, a centre dot means they are a carrier, and a question mark means their status is unknown. An arrow marks the consultand, the person the assessment is about. In generation I a grandmother is a carrier. In generation II one son has the condition and one daughter is a carrier; she and her partner have two children in generation III, the consultand and a sibling; neither has been tested, so both carry a question mark.",
            },
            whatToExpect: {
              body:
                "Your reproductive risk assessment focuses on the information available, what it may suggest, and its limitations.\n\nBefore your appointment\nYou will be asked to complete a family history questionnaire covering health conditions on both sides of your family. If you are unsure about any of it, it is worth asking your relatives what they know. Most people find there is more information available once they ask.\nThe more you can gather, the more detailed and useful your assessment will be. The details that help most are the name of the condition, the age at diagnosis, and, where a relative has died, their age and the cause.\n\nWhat the assessment may include\n• A review of your personal and family health history\n• A multi-generational family tree (pedigree), drawn from the information you provide\n• A discussion of patterns that may suggest an inherited risk\n• A calculated chance of a condition being passed on, where that can be worked out\n• An explanation of relevant testing options and what each can and cannot show\n• Whether your family history or reproductive risk meets the criteria for further genetic testing, including what current guidelines recommend and what is available to you where you live\n• Personalized recommendations and next steps\n\nAfter your appointment\nYou receive a summary package to keep. It includes your pedigree, what the assessment found, and the recommended next steps. It is yours to share as you choose, whether with relatives for whom the findings may also be relevant or with your own healthcare providers.\n\nThe amount and quality of available information affect how specific an assessment can be. A limited or uninformative family history does not rule out genetic risk, and genetic testing may sometimes provide additional information.",
            },
            process: {
              body:
                "Step 1: Initial Consultation\nWe begin with a conversation about your goals, family planning timeline, and specific concerns. We explain what a family-history assessment can and cannot tell you.\n\nStep 2: Family History Collection\nYou will be asked to gather available health information from both sides of your family. Helpful details can include medical conditions, ages at diagnosis, ancestry, pregnancy history, and known genetic test results. We provide a structured intake form to guide you.\n\nStep 3: Pedigree Review\nWhen appropriate, your genetic counsellor organizes the information into a multi-generational family tree (pedigree) and reviews it for patterns that may suggest an inherited risk.\n\nStep 4: Risk Assessment & Recommendations\nWe explain what the available information may mean, where uncertainty remains, and whether additional assessment or testing may be useful.\n\nStep 5: Review & Next Steps\nWe review the findings in clear language, answer your questions, and discuss personalized next steps. With your consent, relevant information can be shared with your family or healthcare providers.",
            },
            faq: {
              items: [
                {
                  q: "Why is a reproductive risk assessment useful to me?",
                  a:
                    "Have you ever wondered whether a condition or characteristic in one of your family members is hereditary? This assessment gives you a better understanding of the chance of your children developing the same conditions present in your family. Some conditions are shaped by environment as well as genes, so knowing early also gives you time to put protective measures in place. And if it points to an increased chance, you may be eligible for publicly funded testing, depending on the programs where you live.",
                },
                {
                  q: "What is a pedigree and why is it important?",
                  a:
                    "A pedigree is a multi-generational family tree that records health conditions, ages at diagnosis, ancestry, pregnancy history, and family relationships. It can help a genetic counsellor recognize patterns that may suggest an inherited risk, but it cannot identify every genetic condition or replace genetic testing when testing is indicated.",
                },
                {
                  q: "I don't know much about my family's medical history. Can I still do the assessment?",
                  a:
                    "Yes. While a more complete family history allows for a more precise assessment, we can still provide valuable insights with whatever information you have. We can also discuss whether genetic testing might be an alternative way to gather information if your family history is limited.",
                },
                {
                  q: "Will the assessment tell me whether I will definitely have a child with a genetic condition?",
                  a:
                    "No assessment can provide absolute certainty. What we provide is a comprehensive evaluation of your potential reproductive risks based on your family history. This helps you understand whether further testing — such as Carrier Screening or PGT — may be beneficial, and helps you make informed decisions about family planning.",
                },
                {
                  q: "Is this assessment useful if I'm already pregnant?",
                  a:
                    "Yes. The assessment gives you the most options before conception, when routes such as IVF with Preimplantation Genetic Testing (PGT), or using donor eggs, sperm, or embryos, are still open. It remains useful during pregnancy. If you are already pregnant, we explain how a family history assessment fits together with carrier screening and prenatal screening, and what each can and cannot add. Timing matters once you are pregnant: several tests have windows tied to gestational age, and results take time to come back, so it is worth getting in touch sooner rather than later.",
                },
                {
                  q: "Can I share the assessment with my family, doctor, or fertility specialist?",
                  a:
                    "Yes, and we encourage it. Your summary package is yours to share, and passing it on is often how the information reaches the people it matters to: relatives who share the same family history, your family doctor, or a fertility specialist who can consider the findings alongside your medical history and other clinical information. What you share, and with whom, is always your decision.",
                },
              ],
            },
          },
          carrierScreening: {
            title: "Proactive Genetic Screening",
            body:
              "Screening for people who would rather know early. Carrier screening estimates the chance of passing an inherited condition to a child, before or during pregnancy. Hereditary cancer screening looks at your own health, for inherited changes that raise the chance of certain cancers.",
            details:
              "Proactive genetic screening is testing you choose while you are well, rather than testing prompted by a symptom or a diagnosis. What it can tell you depends on which kind you choose, and each kind is described below.\n\nScreening describes chance, not certainty. No screen looks at every gene or detects every disease-causing change, and a result means most when it is read alongside your personal and family health history. We go through what a particular test can and cannot tell you before you decide whether to do it.",
            diagram: {
              title: "How these conditions are inherited",
              intro:
                "A child inherits one copy of each gene from the egg and one from the sperm. These diagrams show the possible outcomes for each pregnancy when one or both reproductive partners carry a disease-causing variant associated with the same autosomal recessive condition.",
              legend: {
                working: "Working copy",
                variant: "Copy with a disease-causing change",
              },
              autosomal: {
                heading: "Autosomal recessive conditions",
                note: "Both copies of the gene need a disease-causing change for a child to have the condition.",
              },
              dominant: {
                heading: "Autosomal dominant conditions",
                note: "Only one copy of the gene needs a disease-causing change for a person to be affected, so the condition usually appears in every generation of a family.",
              },
              dominantParent: {
                heading: "When one parent has the condition",
                parentA: "Has the condition",
                parentB: "Does not have it",
                affected: "Has the condition",
                unaffected: "Does not have it",
                note: "Each pregnancy has a 50% chance of a child with the condition. A child who does not inherit the change cannot pass it on. Having the change does not always mean the condition develops, and when it does, its severity and the age it appears can vary within one family.",
              },
              xlinked: {
                heading: "X-linked conditions",
                note: "These conditions involve genes on the X chromosome, so the outcome depends on which sex chromosomes a child inherits. X and Y below mark those chromosomes.",
              },
              xlinkedCarrier: {
                heading: "When the egg provider is a carrier",
                parentA: "Carrier",
                parentB: "Not a carrier",
                twoXCarrier: "Carrier",
                twoXNotCarrier: "Not a carrier",
                oneXAffected: "Has the condition",
                oneXNotAffected: "Not affected",
                note: "Overall, each pregnancy has a 25% chance of a child with the condition. Some carriers with two X chromosomes can have mild or variable symptoms, and the exact chance depends on the condition.",
              },
              bothCarriers: {
                heading: "When both partners are carriers",
                parentA: "Carrier",
                parentB: "Carrier",
                affected: "Has the condition",
                carrier: "Carriers",
                unaffected: "Not a carrier",
                note: "Each pregnancy has a 25% chance of a child with the condition.",
              },
              oneCarrier: {
                heading: "When only one partner is a carrier",
                parentA: "Carrier",
                parentB: "Not a carrier",
                carrier: "Carriers",
                unaffected: "Not carriers",
                note: "There is no increased chance of a child with the condition. This is the most common outcome for couples who complete carrier screening.",
              },
              footnote:
                "These chances are the same for each pregnancy and are not changed by the outcome of a previous pregnancy.",
              description:
                "Three inheritance diagrams. In the first, both reproductive partners are carriers of the same autosomal recessive condition: across four possible outcomes for each pregnancy, one child has the condition, two are carriers, and one inherits neither familial variant — a 25% chance of an affected child, a 50% chance of a carrier, and a 25% chance of neither. In the second, only one partner is a carrier: two of the four possible outcomes are carriers and two inherit neither variant — a 50% chance of a carrier child and no increased chance of a child with the condition. The third shows an X-linked recessive condition where the egg provider is a carrier: of the four possible outcomes, one child with two X chromosomes is a carrier, one is not, one child with one X and one Y has the condition, and one does not — 25% each.",
            },
            screening: {
              carrier: {
                heading: "Carrier Screening",
                body:
                  "Carrier screening is a voluntary genetic test that looks for gene variants associated with inherited conditions that could be passed to a child. It can be performed before or during pregnancy and estimates reproductive chance; it cannot identify every genetic condition or predict every possible health outcome. Conditions commonly included on expanded panels include cystic fibrosis, spinal muscular atrophy, fragile X syndrome, sickle cell disease, and hemophilia.\n\nAutosomal recessive conditions\nMost genes come in pairs — one inherited from the egg and one from the sperm. A person is considered a carrier when one copy of a gene has a disease-causing change. Carriers are typically healthy because the second copy is working.\n\nIf both reproductive partners carry disease-causing variants associated with the same autosomal recessive condition, each pregnancy has:\n• a 25% chance of a child with the condition\n• a 50% chance of a child who is also a carrier\n• a 25% chance of a child who inherits neither familial variant\n\nThese chances are the same for each pregnancy.\n\nIf only one reproductive partner carries a disease-causing variant, there is no increased chance of a child with that condition. Each pregnancy has a 50% chance of a child who is also a carrier and a 50% chance of a child who inherits neither familial variant. This is the most common outcome for couples who complete carrier screening.\n\nX-linked conditions\nThese conditions are associated with genes on the X chromosome. People with one X chromosome are more likely to be affected if that X carries a disease-causing variant. People with two X chromosomes may be carriers, although some carriers can have mild or variable symptoms. For X-linked recessive conditions, an egg provider who is a carrier has an overall 25% chance in each pregnancy of having an affected child; the exact chance depends on the condition and the chromosomes inherited by the child.\n\nBeing a carrier is common, and most carriers have no known family history. Panel size, carrier frequency, and detection rates vary by laboratory and population. For one expanded panel screening more than 500 conditions, the laboratory reports that approximately 70% of people carry at least one finding on the panel, about 4% of tested couples or pregnancies have an increased reproductive chance identified, and the overall detection rate is approximately 95%. These figures are panel-specific and may change.\n\nNo carrier screen detects every disease-causing variant. A negative result reduces — but does not eliminate — the chance of being a carrier; this remaining chance is called residual risk. If there is a known family history of a genetic condition, consultation with a genetic counsellor before testing is recommended.",
                diagram: {
                  footnote: "These chances are the same for each pregnancy and are not changed by the outcome of a previous pregnancy.",
                  description:
                    "Three inheritance diagrams. In the first, both reproductive partners are carriers of the same autosomal recessive condition: across four possible outcomes for each pregnancy, one child has the condition, two are carriers, and one inherits neither familial variant — a 25% chance of an affected child, a 50% chance of a carrier, and a 25% chance of neither. In the second, only one partner is a carrier: two of the four possible outcomes are carriers and two inherit neither variant — a 50% chance of a carrier child and no increased chance of a child with the condition. The third shows an X-linked recessive condition where the egg provider is a carrier: of the four possible outcomes, one child with two X chromosomes is a carrier, one is not, one child with one X and one Y has the condition, and one does not — 25% each.",
                },
              },
              hereditaryCancer: {
                heading: "Hereditary Cancer Screening",
                body:
                  "Some cancers run in families because of an inherited change in a gene that normally helps protect against them. Screening looks for those changes in someone who is currently well, so that a person who carries one can begin surveillance earlier or more often, consider options that reduce their chance, and tell the relatives who may carry the same change.\nMost cancer is not inherited, and carrying one of these changes is not a diagnosis. It describes a higher chance across a lifetime rather than a certainty, and some people who carry a change never develop the condition. Screening is most informative when there is a pattern in the family, such as the same cancer in several relatives, a diagnosis at an unusually young age, or a combination of cancers known to occur together. A family history that looks clear does not rule an inherited change out.\n\nInherited cancer predispositions usually follow an autosomal dominant pattern. Only one of the two copies of a gene needs a disease-causing change for a person to be at increased chance, which is why these conditions often appear in more than one generation of a family. Someone who carries the change has a 50% chance of passing it on with each pregnancy, and a child who does not inherit it cannot pass it on to their own children.\n\nThis is also why the same change can look different from one relative to the next. Which cancers are involved, how likely they are, and the age at which they tend to appear vary by gene and by family, and carrying the change is not on its own a reason to expect any particular outcome.",
                diagram: {
                  footnote: "The 50% applies to each pregnancy on its own. Inheriting the change raises the chance of certain cancers over a lifetime; it is not a diagnosis.",
                  description: "An inheritance diagram. One parent has an autosomal dominant condition, carrying one changed copy of the gene alongside one working copy, and the other parent has two working copies. Across the four possible outcomes for each pregnancy, two children inherit the changed copy and have the condition and two inherit two working copies and do not: a 50% chance either way.",
                },
              },
            },
            faq: {
              items: [
                {
                  q: "What does it mean to be a carrier?",
                  a:
                    "Being a carrier usually means that one copy of a gene has a disease-causing change while the other copy is working. Carriers of autosomal recessive conditions are typically healthy. For most autosomal recessive conditions, both reproductive partners must carry disease-causing variants associated with the same condition for there to be an increased chance of having an affected child.",
                },
                {
                  q: "How common is it to be a carrier?",
                  a:
                    "Being a carrier is common, and most carriers have no known family history. The percentage of people with a finding depends on the genes, conditions, laboratory, and population included in the panel. For one expanded panel screening more than 500 conditions, the laboratory reports that approximately 70% of people have at least one carrier finding.",
                },
                {
                  q: "If I'm a carrier, does that mean my partner needs to be tested too?",
                  a:
                    "For most autosomal recessive conditions, both reproductive partners need to carry disease-causing variants associated with the same condition for there to be an increased chance. If you are found to be a carrier, testing the other reproductive partner is therefore often the next step. X-linked conditions follow a different inheritance pattern, so partner testing may not answer the same question.",
                },
                {
                  q: "What if I am a carrier but my reproductive partner is not?",
                  a:
                    "For an autosomal recessive condition, if only one reproductive partner carries a disease-causing variant, there is no increased chance of a child with that condition. Each pregnancy has a 50% chance of a child who is also a carrier and a 50% chance of a child who inherits neither familial variant. This is the most common outcome for couples who complete carrier screening. Carriers are typically healthy, and a child who is a carrier may wish to consider screening in adulthood when planning their own family. A negative result still reduces — but does not eliminate — the chance of being a carrier.",
                },
                {
                  q: "Can carrier screening detect all genetic conditions?",
                  a:
                    "No. Carrier screening does not include every genetic condition or detect every disease-causing variant in the genes tested. A negative result reduces — but does not eliminate — the chance of being a carrier. The remaining chance is called residual risk, and it varies by condition, laboratory method, and personal or family history.",
                },
                {
                  q: "Should I do carrier screening before or during pregnancy?",
                  a:
                    "Carrier screening can be done either before or during pregnancy. Doing it before pregnancy gives you the widest range of reproductive options if an increased risk is identified. However, it is still valuable during pregnancy — it can help you prepare and inform decisions about prenatal testing.",
                },
                {
                  q: "What are my options if both my partner and I are carriers of the same condition?",
                  a:
                    "For an autosomal recessive condition, if both partners carry disease-causing variants associated with the same condition, each pregnancy usually has a 25% chance of an affected child. Options may include continuing with family plans, IVF with Preimplantation Genetic Testing (PGT) when appropriate, using donor eggs or sperm, prenatal diagnostic testing during pregnancy, or adoption. We discuss all available options in a supportive, non-directive environment.",
                },
              ],
            },
          },
          hiddenScreening: {
            title: "Proactive Genetic Screening",
            body: "Screening for people who would rather know early. Carrier screening estimates the chance of passing an inherited condition to a child, before or during pregnancy. Hereditary cancer screening looks at your own health, for inherited changes that raise the chance of certain cancers.",
            details:
              'Proactive genetic screening answers two different questions. Carrier screening asks what you could pass on to a child. Hereditary cancer screening asks what you may have inherited yourself, and what that means for your own health. They are separate tests, and you can do either or both.\n\nCarrier screening is a voluntary genetic test that looks for gene variants associated with inherited conditions that could be passed to a child. It can be performed before or during pregnancy and estimates reproductive chance; it cannot identify every genetic condition or predict every possible health outcome. Conditions commonly included on expanded panels include cystic fibrosis, spinal muscular atrophy, fragile X syndrome, sickle cell disease, and hemophilia.\n\nAutosomal recessive conditions\nMost genes come in pairs — one inherited from the egg and one from the sperm. A person is considered a carrier when one copy of a gene has a disease-causing change. Carriers are typically healthy because the second copy is working.\n\nIf both reproductive partners carry disease-causing variants associated with the same autosomal recessive condition, each pregnancy has:\n• a 25% chance of a child with the condition\n• a 50% chance of a child who is also a carrier\n• a 25% chance of a child who inherits neither familial variant\n\nThese chances are the same for each pregnancy.\n\nIf only one reproductive partner carries a disease-causing variant, there is no increased chance of a child with that condition. Each pregnancy has a 50% chance of a child who is also a carrier and a 50% chance of a child who inherits neither familial variant. This is the most common outcome for couples who complete carrier screening.\n\nX-linked conditions\nThese conditions are associated with genes on the X chromosome. People with one X chromosome are more likely to be affected if that X carries a disease-causing variant. People with two X chromosomes may be carriers, although some carriers can have mild or variable symptoms. For X-linked recessive conditions, an egg provider who is a carrier has an overall 25% chance in each pregnancy of having an affected child; the exact chance depends on the condition and the chromosomes inherited by the child.\n\nBeing a carrier is common, and most carriers have no known family history. Panel size, carrier frequency, and detection rates vary by laboratory and population. For one expanded panel screening more than 500 conditions, the laboratory reports that approximately 70% of people carry at least one finding on the panel, about 4% of tested couples or pregnancies have an increased reproductive chance identified, and the overall detection rate is approximately 95%. These figures are panel-specific and may change.\n\nNo carrier screen detects every disease-causing variant. A negative result reduces — but does not eliminate — the chance of being a carrier; this remaining chance is called residual risk. If there is a known family history of a genetic condition, consultation with a genetic counsellor before testing is recommended.\n\nHereditary cancer screening\nSome cancers run in families because of an inherited change in a gene that normally helps protect against them. Screening looks for those changes in someone who is currently well, so that a person who carries one can begin surveillance earlier or more often, consider options that reduce their chance, and tell the relatives who may carry the same change.\nMost cancer is not inherited, and carrying one of these changes is not a diagnosis. It describes a higher chance across a lifetime rather than a certainty, and some people who carry a change never develop the condition. Screening is most informative when there is a pattern in the family, such as the same cancer in several relatives, a diagnosis at an unusually young age, or a combination of cancers known to occur together. A family history that looks clear does not rule an inherited change out.',
            diagram: {
              title: "How these conditions are inherited",
              intro:
                "A child inherits one copy of each gene from the egg and one from the sperm. These diagrams show the possible outcomes for each pregnancy when one or both reproductive partners carry a disease-causing variant associated with the same autosomal recessive condition.",
              legend: {
                working: "Working copy",
                variant: "Copy with a disease-causing change",
              },
              autosomal: {
                heading: "Autosomal recessive conditions",
                note: "Both copies of the gene need a disease-causing change for a child to have the condition.",
              },
              dominant: {
                heading: "Autosomal dominant conditions",
                note: "Only one copy of the gene needs a disease-causing change for a person to be affected, so the condition usually appears in every generation of a family.",
              },
              dominantParent: {
                heading: "When one parent has the condition",
                parentA: "Has the condition",
                parentB: "Does not have it",
                affected: "Has the condition",
                unaffected: "Does not have it",
                note: "Each pregnancy has a 50% chance of a child with the condition. A child who does not inherit the change cannot pass it on. Having the change does not always mean the condition develops, and when it does, its severity and the age it appears can vary within one family.",
              },
              xlinked: {
                heading: "X-linked conditions",
                note: "These conditions involve genes on the X chromosome, so the outcome depends on which sex chromosomes a child inherits. X and Y below mark those chromosomes.",
              },
              xlinkedCarrier: {
                heading: "When the egg provider is a carrier",
                parentA: "Carrier",
                parentB: "Not a carrier",
                twoXCarrier: "Carrier",
                twoXNotCarrier: "Not a carrier",
                oneXAffected: "Has the condition",
                oneXNotAffected: "Not affected",
                note: "Overall, each pregnancy has a 25% chance of a child with the condition. Some carriers with two X chromosomes can have mild or variable symptoms, and the exact chance depends on the condition.",
              },
              bothCarriers: {
                heading: "When both partners are carriers",
                parentA: "Carrier",
                parentB: "Carrier",
                affected: "Has the condition",
                carrier: "Carriers",
                unaffected: "Not a carrier",
                note: "Each pregnancy has a 25% chance of a child with the condition.",
              },
              oneCarrier: {
                heading: "When only one partner is a carrier",
                parentA: "Carrier",
                parentB: "Not a carrier",
                carrier: "Carriers",
                unaffected: "Not carriers",
                note: "There is no increased chance of a child with the condition. This is the most common outcome for couples who complete carrier screening.",
              },
              footnote:
                "These chances are the same for each pregnancy and are not changed by the outcome of a previous pregnancy.",
              description:
                "Three inheritance diagrams. In the first, both reproductive partners are carriers of the same autosomal recessive condition: across four possible outcomes for each pregnancy, one child has the condition, two are carriers, and one inherits neither familial variant — a 25% chance of an affected child, a 50% chance of a carrier, and a 25% chance of neither. In the second, only one partner is a carrier: two of the four possible outcomes are carriers and two inherit neither variant — a 50% chance of a carrier child and no increased chance of a child with the condition. The third shows an X-linked recessive condition where the egg provider is a carrier: of the four possible outcomes, one child with two X chromosomes is a carrier, one is not, one child with one X and one Y has the condition, and one does not — 25% each.",
            },
            whatToExpect: {
              body:
                "Carrier screening is a voluntary genetic test that can be done before or during pregnancy.\n\nHow the test works\n• A blood or saliva sample is sent to a laboratory for DNA analysis\n• Results are often available within 2–3 weeks after the laboratory receives the sample, although timing varies\n• Your genetic counsellor reviews what the test can and cannot detect and explains the results in clear language\n\nWhat the test looks for\nExpanded panels can include hundreds of autosomal recessive and X-linked conditions. The exact genes, conditions, carrier frequencies, and detection rates depend on the laboratory and panel selected.\n\nUnderstanding your results\n• A negative result reduces — but does not eliminate — the chance that you are a carrier. The remaining chance is called residual risk.\n• A positive result means the laboratory identified a disease-causing or likely disease-causing variant associated with a specific condition. The reproductive chance depends on the condition, inheritance pattern, and partner's result.\n• For an autosomal recessive condition, if both partners carry disease-causing variants associated with the same condition, each pregnancy usually has a 25% chance of an affected child.\n• If only one partner carries a variant associated with an autosomal recessive condition, there is no increased chance of an affected child. Each pregnancy has a 50% chance of a child who is also a carrier.\n• If an increased reproductive chance is identified, we discuss the available options in a supportive, non-directive environment.",
            },
            process: {
              body:
                "Step 1: Pre-Test Consultation\nBefore testing, your genetic counsellor explains what carrier screening can and cannot detect, reviews your personal and family history, and helps you decide whether a panel is appropriate. If you have a known family history of a genetic condition, targeted assessment may be more appropriate than routine screening alone.\n\nStep 2: Sample Collection\nA blood or saliva sample is collected and sent to the selected laboratory for analysis.\n\nStep 3: Laboratory Analysis\nThe laboratory analyzes the genes included on the selected panel. Panel size and turnaround time vary; results are often available within 2–3 weeks after the laboratory receives the sample.\n\nStep 4: Results Review\nYour genetic counsellor explains the findings and their limitations. If you carry a variant associated with an autosomal recessive condition, testing the other reproductive partner is often the next step. We discuss the condition, inheritance pattern, residual risk, and what the result may mean for family planning.\n\nStep 5: Reproductive Options (if applicable)\nIf results identify an increased chance of having a child with a genetic condition, options may include:\n• Continuing with family plans without changes\n• IVF with Preimplantation Genetic Testing (PGT), when appropriate\n• Using donor eggs, sperm, or embryos\n• Prenatal diagnostic testing during pregnancy\n• Adoption\n\nWe discuss all available options in a supportive, non-directive environment.",
            },
            faq: {
              items: [
                {
                  q: "What does it mean to be a carrier?",
                  a:
                    "Being a carrier usually means that one copy of a gene has a disease-causing change while the other copy is working. Carriers of autosomal recessive conditions are typically healthy. For most autosomal recessive conditions, both reproductive partners must carry disease-causing variants associated with the same condition for there to be an increased chance of having an affected child.",
                },
                {
                  q: "How common is it to be a carrier?",
                  a:
                    "Being a carrier is common, and most carriers have no known family history. The percentage of people with a finding depends on the genes, conditions, laboratory, and population included in the panel. For one expanded panel screening more than 500 conditions, the laboratory reports that approximately 70% of people have at least one carrier finding.",
                },
                {
                  q: "If I'm a carrier, does that mean my partner needs to be tested too?",
                  a:
                    "For most autosomal recessive conditions, both reproductive partners need to carry disease-causing variants associated with the same condition for there to be an increased chance. If you are found to be a carrier, testing the other reproductive partner is therefore often the next step. X-linked conditions follow a different inheritance pattern, so partner testing may not answer the same question.",
                },
                {
                  q: "What if I am a carrier but my reproductive partner is not?",
                  a:
                    "For an autosomal recessive condition, if only one reproductive partner carries a disease-causing variant, there is no increased chance of a child with that condition. Each pregnancy has a 50% chance of a child who is also a carrier and a 50% chance of a child who inherits neither familial variant. This is the most common outcome for couples who complete carrier screening. Carriers are typically healthy, and a child who is a carrier may wish to consider screening in adulthood when planning their own family. A negative result still reduces — but does not eliminate — the chance of being a carrier.",
                },
                {
                  q: "Can carrier screening detect all genetic conditions?",
                  a:
                    "No. Carrier screening does not include every genetic condition or detect every disease-causing variant in the genes tested. A negative result reduces — but does not eliminate — the chance of being a carrier. The remaining chance is called residual risk, and it varies by condition, laboratory method, and personal or family history.",
                },
                {
                  q: "Should I do carrier screening before or during pregnancy?",
                  a:
                    "Carrier screening can be done either before or during pregnancy. Doing it before pregnancy gives you the widest range of reproductive options if an increased risk is identified. However, it is still valuable during pregnancy — it can help you prepare and inform decisions about prenatal testing.",
                },
                {
                  q: "What are my options if both my partner and I are carriers of the same condition?",
                  a:
                    "For an autosomal recessive condition, if both partners carry disease-causing variants associated with the same condition, each pregnancy usually has a 25% chance of an affected child. Options may include continuing with family plans, IVF with Preimplantation Genetic Testing (PGT) when appropriate, using donor eggs or sperm, prenatal diagnostic testing during pregnancy, or adoption. We discuss all available options in a supportive, non-directive environment.",
                },
              ],
            },
          },
          prenatalScreening: {
            title: "Prenatal Screening",
            body:
              "Prenatal screening is several different tests, and which ones you are offered depends on where you live. We explain what each one looks for, what it can and cannot tell you, and what is publicly funded where you are, so you can decide which to do and which to skip.",
            details:
              "Prenatal screening estimates the chance that a pregnancy is affected by certain conditions. Unlike carrier screening, which looks for variants in a reproductive partner's genes, prenatal screening focuses on the pregnancy itself. What is screened for depends on the option: some look at chromosomes, one also screens for open neural tube defects, and ultrasound looks at how the pregnancy is developing.\n\nWhich options are offered, and which are publicly funded, varies by province and by country. The options below are the ones typically offered, rather than a complete list: a local program may combine them differently or use its own name for them. We explain what is available where you live, what each can and cannot detect, and your option to decline any of it.\n\nScreening vs. Diagnostic Testing\nA screening test estimates chance; it does not confirm whether a condition is present. Diagnostic testing, such as chorionic villus sampling (CVS) or amniocentesis, can determine whether the specific chromosome condition tested is present. We explain the benefits, limitations, and possible risks of each option, including the option not to test.\n\nInterpreting Your Results\nA high-chance or atypical screening result does not confirm that the pregnancy has a condition. We explain the result, estimate its positive predictive value (PPV) in context, and discuss genetic counselling, detailed ultrasound, and diagnostic testing with CVS or amniocentesis. A low-chance result lowers the chance of the conditions screened but does not reduce it to zero.",
            diagram: {
              title: "What NIPT actually measures",
              intro:
                "NIPT uses a single blood sample from the pregnant person. That sample carries short fragments of cell-free DNA, and they do not all come from the same place.",
              legend: {
                maternal: "Cell-free DNA from the pregnant person",
                placental: "Cell-free DNA from the placenta",
              },
              sampleLabel: "One blood sample",
              scaleNote:
                "Illustrative proportions — the placental share differs between pregnancies.",
              notesArray: [
                "Most of the cell-free DNA in the sample comes from the pregnant person, not the pregnancy.",
                "A smaller share comes from the placenta. This is the part the screen relies on, which is why NIPT reflects the placenta rather than the fetus directly.",
                "When the placental share is too small, the laboratory may not be able to give a result. This happens in approximately 1–3% of cases and is more common at a higher body mass index.",
              ],
              footnote:
                "Because NIPT measures placental DNA, a result can occasionally differ from the fetus's own chromosomes. This is one reason an increased-chance result is confirmed with diagnostic testing.",
              description:
                "A diagram of a single blood sample containing many short cell-free DNA fragments. Most fragments come from the pregnant person; a small minority, scattered throughout, come from the placenta. NIPT relies on that smaller placental share, so it reflects the placenta rather than the fetus directly, and when the placental share is too small — approximately 1 to 3% of cases, more common at a higher body mass index — the laboratory may not be able to give a result.",
            },
            screening: {
              nipt: {
                heading: "Non-Invasive Prenatal Testing (NIPT)",
                body:
                  "NIPT, also called cell-free DNA screening, analyzes cell-free DNA fragments circulating in the pregnant person's blood. Most of these fragments come from the pregnant person and a smaller proportion comes from the placenta; the test uses the placental fraction to estimate the chance of a chromosome condition. It can usually be performed from 10 weeks of pregnancy.\nNIPT is the most sensitive and specific screening test for the common trisomies, Down syndrome (Trisomy 21), Edwards syndrome (Trisomy 18), and Patau syndrome (Trisomy 13), but false-positive, false-negative, and no-result outcomes can occur. It does not screen for every chromosome or structural condition, and it does not screen for open neural tube defects, so ultrasound and other recommended prenatal care remain important.\n\nWhat NIPT commonly screens for\n• Down syndrome (Trisomy 21)\n• Edwards syndrome (Trisomy 18)\n• Patau syndrome (Trisomy 13)\n• Triploidy (an extra copy of every chromosome), on some panels\n• Sex chromosome differences, when selected as an optional add-on\n\nSome laboratories also offer screening for selected microdeletions. These conditions are rare, a high-chance result may have a substantial chance of being a false positive, and routine population screening for microdeletions is not recommended. Anyone considering this option should receive pre-test counselling and understand that diagnostic testing is needed to confirm a result.\n\nNIPT is a blood-based screening test that can usually be performed from 10 weeks of pregnancy. For Trisomy 21, some validated tests report a detection rate above 99% and a false-positive rate below 0.1%; performance varies by condition, laboratory, and clinical circumstances. Results are often available within 5–10 days after sample receipt, depending on the laboratory.\n\nIn approximately 1–3% of cases the laboratory cannot produce a result, most often because too little placental DNA is present in the sample. This is more common at a higher body mass index. A repeat blood draw is usually offered, and some laboratories offer a refund instead. A no-result outcome is not an abnormal result, but it is worth discussing before deciding on next steps.",
                diagram: {
                  footnote: "Because NIPT measures placental DNA, a result can occasionally differ from the fetus's own chromosomes. This is one reason an increased-chance result is confirmed with diagnostic testing.",
                  description:
                    "A diagram of a single blood sample containing many short cell-free DNA fragments. Most fragments come from the pregnant person; a small minority, scattered throughout, come from the placenta. NIPT relies on that smaller placental share, so it reflects the placenta rather than the fetus directly, and when the placental share is too small — approximately 1 to 3% of cases, more common at a higher body mass index — the laboratory may not be able to give a result.",
                },
              },
              fts: {
                heading: "First Trimester Screening (FTS)",
                body:
                  "FTS combines an ultrasound measurement taken at the back of the fetal neck, called the nuchal translucency, with markers measured in the pregnant person's blood. It is performed between about 11 and 14 weeks and estimates the chance of Down syndrome and Edwards syndrome. It identifies fewer affected pregnancies than NIPT and returns more false-positive results, but it is widely available and, in many regions, publicly funded where NIPT is not.",
              },
              serum: {
                heading: "Maternal Serum Screening",
                body:
                  "Serum screening measures proteins and hormones in the pregnant person's blood, usually between about 15 and 20 weeks. It estimates the chance of Down syndrome and Edwards syndrome, and unlike NIPT it also screens for open neural tube defects such as spina bifida. Some programs combine a first-trimester and a second-trimester sample into a single result, which performs better than either sample on its own.",
              },
              ultrasound: {
                heading: "Ultrasound",
                body:
                  "Ultrasound looks at how the pregnancy is developing rather than at chromosomes. A dating scan confirms how far along the pregnancy is, the nuchal translucency measurement is taken between about 11 and 14 weeks, and a detailed anatomy scan is usually offered between about 18 and 22 weeks. Ultrasound can identify structural differences that no blood test detects, and it can find features that change the chance of a chromosome condition. On its own it cannot rule a genetic condition in or out.",
              },
            },
            faq: {
              items: [
                {
                  q: "What is the difference between screening and diagnostic testing?",
                  a:
                    "A screening test estimates the chance of a condition; it cannot confirm whether the condition is present. Diagnostic testing with CVS or amniocentesis can determine whether the specific condition tested is present. These procedures carry a small procedure-related miscarriage risk that varies by procedure and centre, so your prenatal care provider should explain the estimate applicable to you.",
                },
                {
                  q: "How accurate is NIPT?",
                  a:
                    "NIPT is the most sensitive and specific screening test for the common trisomies. For Trisomy 21, some validated tests report detection above 99% and false-positive rates below 0.1%, but performance varies by condition, laboratory, and clinical circumstances. NIPT is not diagnostic: false-positive, false-negative, and no-result outcomes can occur, and an increased-chance result should be followed by genetic counselling and an offer of diagnostic testing.",
                },
                {
                  q: "When can I do prenatal screening?",
                  a:
                    "It depends on the option. NIPT can usually be performed from 10 weeks of pregnancy, and results are often available within 5–10 days after the laboratory receives the sample. First Trimester Screening is done between about 11 and 14 weeks, because it combines bloodwork with a nuchal translucency measurement that can only be taken in that window. Maternal serum screening is usually done between about 15 and 20 weeks. Ultrasound runs alongside these: a dating scan early on, the nuchal translucency measurement between about 11 and 14 weeks, and a detailed anatomy scan usually offered between about 18 and 22 weeks. Diagnostic testing has its own windows, with CVS usually offered between about 11 and 14 weeks and amniocentesis from about 15 weeks. If a window has passed, some options close, so it is worth asking early rather than guessing.",
                },
                {
                  q: "What happens if my NIPT does not give a result?",
                  a:
                    "In approximately 1–3% of cases the laboratory cannot produce a result, most often because too little placental DNA is present in the sample. This is more common at a higher body mass index. A repeat blood draw is usually offered, and some laboratories offer a refund instead. A no-result outcome is not an abnormal result, but it is worth discussing with your genetic counsellor or prenatal care provider, who can review whether a redraw, ultrasound, or a different screening or diagnostic option best suits your circumstances.",
                },
                {
                  q: "Does NIPT screen for everything?",
                  a:
                    "No. NIPT primarily screens for Trisomy 21, Trisomy 18, and Trisomy 13. Sex chromosome screening may be offered as an opt-in choice with appropriate counselling. Some laboratories offer selected microdeletion screening, but routine population screening for microdeletions is not recommended because performance is limited for these rare conditions. NIPT does not assess every chromosome condition, single-gene condition, or structural difference, so recommended ultrasound and prenatal care remain important.",
                },
                {
                  q: "What happens if I get a high-risk result?",
                  a:
                    "An increased-chance result does not confirm that the pregnancy has a condition. We explain the result and its limitations, estimate its positive predictive value (PPV) in context, and discuss genetic counselling, detailed ultrasound, and diagnostic testing with CVS or amniocentesis. The decision to pursue or decline further testing is yours.",
                },
                {
                  q: "I did PGT-A during IVF. Do I still need prenatal screening?",
                  a:
                    "PGT-A reduces but does not eliminate the chance of chromosome differences, and it cannot identify every genetic or structural condition. Prenatal screening and diagnostic testing should still be discussed after PGT-A. The most appropriate option depends on your individual circumstances, goals, and preferences, and recommended prenatal ultrasound remains important.",
                },
              ],
            },
          },
          hiddenPrenatal: {
            title: "Prenatal Screening",
            body:
              "All pregnant individuals — regardless of age or baseline chance — should be offered clear information about prenatal screening and diagnostic testing, including the option to decline. GeneFlou explains the choices available in your region and supports shared decision-making based on your values, circumstances, and preferences.",
            details:
              'Prenatal screening estimates the chance that a pregnancy is affected by certain conditions. Unlike carrier screening, which looks for variants in a reproductive partner\'s genes, prenatal screening focuses on the pregnancy itself. What is screened for depends on the option: some look at chromosomes, one also screens for open neural tube defects, and ultrasound looks at how the pregnancy is developing.\n\nWhich options are offered, and which are publicly funded, varies by province and by country. The options below are the ones typically offered, rather than a complete list: a local program may combine them differently or use its own name for them. We explain what is available where you live, what each can and cannot detect, and your option to decline any of it.\n\nNon-Invasive Prenatal Testing (NIPT)\nNIPT, also called cell-free DNA screening, analyzes cell-free DNA fragments circulating in the pregnant person\'s blood. Most of these fragments come from the pregnant person and a smaller proportion comes from the placenta; the test uses the placental fraction to estimate the chance of a chromosome condition. It can usually be performed from 10 weeks of pregnancy.\nNIPT is the most sensitive and specific screening test for the common trisomies, Down syndrome (Trisomy 21), Edwards syndrome (Trisomy 18), and Patau syndrome (Trisomy 13), but false-positive, false-negative, and no-result outcomes can occur. It does not screen for every chromosome or structural condition, and it does not screen for open neural tube defects, so ultrasound and other recommended prenatal care remain important.\n\nFirst Trimester Screening (FTS)\nFTS combines an ultrasound measurement taken at the back of the fetal neck, called the nuchal translucency, with markers measured in the pregnant person\'s blood. It is performed between about 11 and 14 weeks and estimates the chance of Down syndrome and Edwards syndrome. It identifies fewer affected pregnancies than NIPT and returns more false-positive results, but it is widely available and, in many regions, publicly funded where NIPT is not.\n\nMaternal Serum Screening\nSerum screening measures proteins and hormones in the pregnant person\'s blood, usually between about 15 and 20 weeks. It estimates the chance of Down syndrome and Edwards syndrome, and unlike NIPT it also screens for open neural tube defects such as spina bifida. Some programs combine a first-trimester and a second-trimester sample into a single result, which performs better than either sample on its own.\n\nUltrasound\nUltrasound looks at how the pregnancy is developing rather than at chromosomes. A dating scan confirms how far along the pregnancy is, the nuchal translucency measurement is taken between about 11 and 14 weeks, and a detailed anatomy scan is usually offered between about 18 and 22 weeks. Ultrasound can identify structural differences that no blood test detects, and it can find features that change the chance of a chromosome condition. On its own it cannot rule a genetic condition in or out.\n\nScreening vs. Diagnostic Testing\nA screening test estimates chance; it does not confirm whether a condition is present. Diagnostic testing, such as chorionic villus sampling (CVS) or amniocentesis, can determine whether the specific chromosome condition tested is present. We explain the benefits, limitations, and possible risks of each option, including the option not to test.\n\nInterpreting Your Results\nA high-chance or atypical screening result does not confirm that the pregnancy has a condition. We explain the result, estimate its positive predictive value (PPV) in context, and discuss genetic counselling, detailed ultrasound, and diagnostic testing with CVS or amniocentesis. A low-chance result lowers the chance of the conditions screened but does not reduce it to zero.',
            diagram: {
              title: "What NIPT actually measures",
              intro:
                "NIPT uses a single blood sample from the pregnant person. That sample carries short fragments of cell-free DNA, and they do not all come from the same place.",
              legend: {
                maternal: "Cell-free DNA from the pregnant person",
                placental: "Cell-free DNA from the placenta",
              },
              sampleLabel: "One blood sample",
              scaleNote:
                "Illustrative proportions — the placental share differs between pregnancies.",
              notesArray: [
                "Most of the cell-free DNA in the sample comes from the pregnant person, not the pregnancy.",
                "A smaller share comes from the placenta. This is the part the screen relies on, which is why NIPT reflects the placenta rather than the fetus directly.",
                "When the placental share is too small, the laboratory may not be able to give a result. This happens in approximately 1–3% of cases and is more common at a higher body mass index.",
              ],
              footnote:
                "Because NIPT measures placental DNA, a result can occasionally differ from the fetus's own chromosomes. This is one reason an increased-chance result is confirmed with diagnostic testing.",
              description:
                "A diagram of a single blood sample containing many short cell-free DNA fragments. Most fragments come from the pregnant person; a small minority, scattered throughout, come from the placenta. NIPT relies on that smaller placental share, so it reflects the placenta rather than the fetus directly, and when the placental share is too small — approximately 1 to 3% of cases, more common at a higher body mass index — the laboratory may not be able to give a result.",
            },
            whatToExpect: {
              body:
                "Prenatal screening estimates the chance of certain chromosome conditions during pregnancy.\n\nWhat NIPT commonly screens for\n• Down syndrome (Trisomy 21)\n• Edwards syndrome (Trisomy 18)\n• Patau syndrome (Trisomy 13)\n• Triploidy (an extra copy of every chromosome), on some panels\n• Sex chromosome differences, when selected as an optional add-on\n\nSome laboratories also offer screening for selected microdeletions. These conditions are rare, a high-chance result may have a substantial chance of being a false positive, and routine population screening for microdeletions is not recommended. Anyone considering this option should receive pre-test counselling and understand that diagnostic testing is needed to confirm a result.\n\nNon-Invasive Prenatal Testing (NIPT)\nNIPT is a blood-based screening test that can usually be performed from 10 weeks of pregnancy. For Trisomy 21, some validated tests report a detection rate above 99% and a false-positive rate below 0.1%; performance varies by condition, laboratory, and clinical circumstances. Results are often available within 5–10 days after sample receipt, depending on the laboratory.\n\nIn approximately 1–3% of cases the laboratory cannot produce a result, most often because too little placental DNA is present in the sample. This is more common at a higher body mass index. A repeat blood draw is usually offered, and some laboratories offer a refund instead. A no-result outcome is not an abnormal result, but it is worth discussing before deciding on next steps.\n\nScreening vs. Diagnostic Testing\nScreening estimates chance and cannot provide a diagnosis. If a result indicates an increased chance, genetic counselling, detailed ultrasound, and diagnostic testing with CVS or amniocentesis should be discussed.",
            },
            process: {
              body:
                "Step 1: Pre-Screening Consultation\nYour genetic counsellor explains the prenatal screening and diagnostic options available in your region, what each can and cannot detect, and the option to decline testing.\n\nStep 2: Choosing an Option\nDepending on availability and your preferences, options may include NIPT (cell-free DNA screening), First Trimester Screening (FTS), another maternal serum screen, or diagnostic testing. Sex chromosome screening is an optional choice that requires pre-test counselling. Routine population screening for microdeletions is not recommended.\n\nStep 3: Sample Collection\nNIPT uses a blood sample and can usually be performed from 10 weeks. FTS combines bloodwork with an ultrasound, generally between 11 and 14 weeks. Timing and logistics depend on the selected test and local healthcare system.\n\nStep 4: Results & Interpretation\nNIPT results are often available within 5–10 days after sample receipt, while FTS timing varies by program. A low-chance result reduces but does not eliminate the chance of the conditions screened. For a high-chance, atypical, or no-result outcome, we explain the limitations, estimate PPV where applicable, and discuss appropriate next steps.\n\nStep 5: Diagnostic Testing (if appropriate)\nAfter an increased-chance or inconclusive screen, diagnostic testing with CVS or amniocentesis may be offered. We explain what each procedure can test, its timing, limitations, and small procedure-related risks so you can make an informed choice.",
            },
            faq: {
              items: [
                {
                  q: "What is the difference between screening and diagnostic testing?",
                  a:
                    "A screening test estimates the chance of a condition; it cannot confirm whether the condition is present. Diagnostic testing with CVS or amniocentesis can determine whether the specific condition tested is present. These procedures carry a small procedure-related miscarriage risk that varies by procedure and centre, so your prenatal care provider should explain the estimate applicable to you.",
                },
                {
                  q: "How accurate is NIPT?",
                  a:
                    "NIPT is the most sensitive and specific screening test for the common trisomies. For Trisomy 21, some validated tests report detection above 99% and false-positive rates below 0.1%, but performance varies by condition, laboratory, and clinical circumstances. NIPT is not diagnostic: false-positive, false-negative, and no-result outcomes can occur, and an increased-chance result should be followed by genetic counselling and an offer of diagnostic testing.",
                },
                {
                  q: "When can I do prenatal screening?",
                  a:
                    "It depends on the option. NIPT can usually be performed from 10 weeks of pregnancy, and results are often available within 5–10 days after the laboratory receives the sample. First Trimester Screening is done between about 11 and 14 weeks, because it combines bloodwork with a nuchal translucency measurement that can only be taken in that window. Maternal serum screening is usually done between about 15 and 20 weeks. Ultrasound runs alongside these: a dating scan early on, the nuchal translucency measurement between about 11 and 14 weeks, and a detailed anatomy scan usually offered between about 18 and 22 weeks. Diagnostic testing has its own windows, with CVS usually offered between about 11 and 14 weeks and amniocentesis from about 15 weeks. If a window has passed, some options close, so it is worth asking early rather than guessing.",
                },
                {
                  q: "What happens if my NIPT does not give a result?",
                  a:
                    "In approximately 1–3% of cases the laboratory cannot produce a result, most often because too little placental DNA is present in the sample. This is more common at a higher body mass index. A repeat blood draw is usually offered, and some laboratories offer a refund instead. A no-result outcome is not an abnormal result, but it is worth discussing with your genetic counsellor or prenatal care provider, who can review whether a redraw, ultrasound, or a different screening or diagnostic option best suits your circumstances.",
                },
                {
                  q: "Does NIPT screen for everything?",
                  a:
                    "No. NIPT primarily screens for Trisomy 21, Trisomy 18, and Trisomy 13. Sex chromosome screening may be offered as an opt-in choice with appropriate counselling. Some laboratories offer selected microdeletion screening, but routine population screening for microdeletions is not recommended because performance is limited for these rare conditions. NIPT does not assess every chromosome condition, single-gene condition, or structural difference, so recommended ultrasound and prenatal care remain important.",
                },
                {
                  q: "What happens if I get a high-risk result?",
                  a:
                    "An increased-chance result does not confirm that the pregnancy has a condition. We explain the result and its limitations, estimate its positive predictive value (PPV) in context, and discuss genetic counselling, detailed ultrasound, and diagnostic testing with CVS or amniocentesis. The decision to pursue or decline further testing is yours.",
                },
                {
                  q: "I did PGT-A during IVF. Do I still need prenatal screening?",
                  a:
                    "PGT-A reduces but does not eliminate the chance of chromosome differences, and it cannot identify every genetic or structural condition. Prenatal screening and diagnostic testing should still be discussed after PGT-A. The most appropriate option depends on your individual circumstances, goals, and preferences, and recommended prenatal ultrasound remains important.",
                },
              ],
            },
          },
          learnMore: "Learn More →",
        },
      },
      team: {
        title: "GeneFlou Team",
        olive: {
          name: "Olive Zhu",
          role: "Co-Founder & Genetic Counsellor",
          bio:
            "Olive is a Canadian certified Genetic Counsellor and Clinical Instructor in the Department of Medical Genetics at the University of British Columbia. Her expertise spans preconception genetic testing and prenatal genetics, and her professional experience across Canada, the UK, the USA, and China fuels a strong interest in the evolving genetic counselling landscape throughout Asia.",
        },
        chris: {
          name: "Christopher Chan",
          role: "Co-Founder",
          bio:
            "Chris co-founded GeneFlou with Olive and oversees its website, operations, outreach, and growth. Being Chinese himself, he knows how much more naturally these conversations unfold in a family's first language, and his ambition for GeneFlou is to make thoughtful, high-quality counselling as readily available in Cantonese and Mandarin as it is in English.",
        },
      },
      contact: {
        title: "Keep in touch?",
        description:
          "Reach out to us directly at genetics@geneflou.com or fill out the form below!",
        form: {
          nameLabel: "Name",
          namePlaceholder: "Name",
          emailLabel: "Email",
          emailPlaceholder: "Email",
          messageLabel: "Message",
          messagePlaceholder: "Type your message here...",
          submit: "Send Message",
        },
      },
      language: {
        label: "Lang",
        selector: "Choose a language",
        english: "English",
        simplified: "Simplified Chinese",
        traditional: "Traditional Chinese",
        shortEnglish: "EN",
        shortSimplified: "简体",
        shortTraditional: "繁體",
      },
      gate: {
        title: "This page is not public",
        prompt:
          "It is a work in progress. Enter the password to continue, or head back to the main site.",
        label: "Password",
        submit: "Continue",
        error: "That password is not right. Try again.",
      },
      nav: {
        home: "Home",
        services: "Services",
        resources: "Resources",
        backToTop: "Back to top",
      },
      // Copy for the resources page. The organisations' own names are not here
      // — they are proper nouns and live in views/Resources/resources-config.js,
      // one string rather than three. What is translated is everything the
      // reader reads *about* a link.
      //
      // (This `resources` is a translation key. The `resources` object it
      // eventually sits inside is i18next's own config option, two levels up.)
      resources: {
        hero: {
          title: "Resources",
          lede: "Places we point people to when a question reaches past what one consultation can cover — how to find a counsellor, what British Columbia offers, and where to read more.",
        },
        opensInNewTab: "opens in a new tab",
        group: {
          findCounsellor: {
            title: "Finding a genetic counsellor",
            entry: {
              cbgc: "Search the register of Canadian Certified Genetic Counsellors. The CBGC is the body that certifies genetic counsellors in Canada.",
              nsgc: "Over 3,300 counsellors across Canada and the United States, in person or by telehealth. A directory rather than a referral service.",
              bcWomens:
                "The provincial genetics clinic for British Columbia and the Yukon, excluding Vancouver Island. A referral from a care provider is required.",
            },
          },
          prenatalBc: {
            title: "Prenatal screening in British Columbia",
            entry: {
              perinatalServicesBc:
                "What prenatal genetic screening the province offers and how to choose between the options. Free with MSP coverage, and always your choice.",
              gecKoPrenatalGuide:
                "A plain-language guide to what prenatal screening looks for, and what a result does and does not mean.",
              gecKoNipt:
                "How non-invasive prenatal testing works, what it can screen for, and why a positive result needs confirming.",
            },
          },
          understandingGenetics: {
            title: "Understanding genetics",
            entry: {
              medlinePlus:
                "The US National Library of Medicine's guide to more than 1,300 conditions, with a \"Help Me Understand Genetics\" primer for readers starting from scratch.",
              gecKoBasics:
                "Genes, chromosomes and inheritance from the beginning, written for readers with no background in biology.",
              gard: "A US National Institutes of Health database of rare and genetic conditions, with information specialists who answer questions directly.",
              gecKoGnda:
                "What Canadian law says about who may ask for your genetic test results — insurers and employers included.",
            },
          },
          livingWithCondition: {
            title: "Living with a genetic condition",
            entry: {
              rareDiseaseFoundation:
                "Vancouver-based peer support and mental health programming, including for families still living without a diagnosis.",
              cord: "The national network of Canadian rare disorder organisations, and a way to find the one for a particular condition.",
              unique:
                "Free downloadable guides to individual chromosome and gene disorders, in several languages. Based in the UK, serving families internationally.",
            },
          },
        },
        disclaimer: {
          title: "About these links",
          body: "These are independent organisations. GeneFlou does not control what they publish, and a page that is accurate today may change. Nothing linked here is a substitute for a consultation about your own health, pregnancy or family history — if something you read raises a question, bring it to us or to your care provider.",
        },
      },
    },
  },
  "zh-Hans": {
    translation: {
      header: {
        welcome: "欢迎来到",
        tagline: "助力家庭茁壮成长。",
        cta: "预约咨询",
      },
      landing: {
        servicesTitle: "我们的服务",
        servicesDescription:
          "GeneFlou 是一家遗传咨询服务机构，致力于帮助成长中的家庭了解其基因检测选择，并理解相关的生育风险。我们通过提供粤语、普通话和英语服务，努力填补华人社区在遗传咨询方面的服务空白。",
        haveQuestions: "有疑问？",
        contactPrompt: "联系我们以了解更多关于我们的服务及我们如何为您提供帮助。",
        getInTouch: "联系我们",
        serviceSection: {
          overview: { title: "服务概览" },
          whatToExpect: { title: "服务流程与预期" },
          process: { title: "服务流程" },
          faq: { title: "常见问题" },
          onThisPage: "本页内容",
        },

        service: {
          geneticCounselling: {
            title: "遗传咨询",
            body:
              "遗传咨询是帮助人们理解并适应遗传因素对健康和疾病所带来的医学、心理及家庭影响的过程。在 GeneFlou，我们提供信息与支持，帮助个人和家庭就基因检测及自身健康作出知情决定。",
            details:
              "遗传咨询师是接受过医学遗传学和咨询专业教育的医疗专业人员。他们会回顾个人和家族健康史，帮助人们理解遗传风险，解释基因检测能提供和不能提供的信息，并讨论检测结果在医学和情绪方面的影响。\n\nGeneFlou 提供英语、普通话和粤语个性化咨询，重点涵盖生殖健康、产前筛查和遗传性疾病。我们的目标是提供清晰信息和支持性、非指导性的咨询，使您的决定符合自身需要、价值观和家庭情况。",
            whenToReachOut: "何时联系我们",
            whenToReachOutSubtitle: "如果您符合以下情况，GeneFlou 的咨询服务可能会对您特别有帮助：",
            whenToReachOutBulletsArray: [
              "收到了基因筛查报告，需要帮助解读结果。",
              "怀疑存在遗传疾病或对家族健康史有担忧。",
              "正在考虑备孕或产前检测，想了解相关选项。",
              "需要能用普通话或粤语流利提供的专业护理和指导。",
              "是一位积极规划的人，相信为意外做好准备，希望在人生新篇章开始之前对自己的遗传健康有清晰、全面的了解。",
            ],
            whatToExpect: {
              body:
                "我们的咨询旨在为您提供一次支持性、无评判的对话，根据您的独特情况和关切量身定制。\n\n咨询过程中会发生什么\n• 全面回顾您个人和家族的病史\n• 用通俗易懂的语言解释遗传学概念\n• 讨论可用的基因检测选项及其能告诉您什么、不能告诉您什么\n• 在您考虑各种选项时提供情感支持和指导\n• 提供个性化的总结和建议及后续步骤\n\n咨询通常为 30–60 分钟，以线上形式进行，支持英语、普通话或粤语。欢迎伴侣或家人一同参加。没有问题是微不足道的，我们鼓励您带着任何疑问前来。",
            },
            process: {
              body:
                "第1步：联系我们\n请通过 genetics@geneflou.com 或我们的网站联系我们表达您的意向。我们会回复您，安排您的咨询，并收集您可以提供的初步信息。\n\n第2步：咨询前信息收集\n在预约之前，我们会向您发送一份简短的表格，用于收集您的个人和家族病史。这让我们能够提前准备，充分利用您的咨询时间。\n\n第3步：咨询\n在咨询过程中，您的遗传咨询师将回顾您的病史，讨论您的关切，解释相关的遗传学概念，并概述您的检测选项和生育风险。这是一次双向对话——您的价值观和偏好将引导讨论方向。\n\n第4步：基因检测（如适用）\n如果您决定进行基因检测，我们会说明在您所在地区如何安排检测，并为您指出可以开具检测的医疗人员，例如家庭医生或专科医生。我们会帮助您了解检测的内容和预期结果时间。\n\n第5步：结果与后续跟进\n结果出来后，您的遗传咨询师将用清晰、富有同理心的语言为您解读。我们会讨论对您和家人的影响，探索您的选项，并支持您对后续步骤做出明智决定。您还会收到一份详细的书面咨询摘要，其中包含相关建议。这份摘要归您所有，您可以自行决定与谁分享，无论是可能同样相关的亲属，还是您自己的医疗服务提供者。",
            },
            faq: {
              items: [
                {
                  q: "什么是遗传咨询师？",
                  a:
                    "遗传咨询师是接受过医学遗传学和咨询专业训练的医疗专业人员。他们会回顾个人和家族健康史，解释遗传风险和检测选项，解读结果，并在人们作出知情决定时提供支持。GeneFlou 的遗传咨询服务由一位获得加拿大认证的遗传咨询师提供。",
                },
                {
                  q: "我需要转介才能预约咨询吗？",
                  a:
                    "不需要转介。您可以直接通过 genetics@geneflou.com 联系我们预约。不过，如果您有之前的基因检测结果或病历，我们可能会请您提前提供，以便我们提供最有针对性的指导。",
                },
                {
                  q: "咨询提供哪些语言？",
                  a:
                    "我们提供英语、普通话和粤语咨询。我们创立 GeneFlou 的初衷正是为了填补华人社区在遗传咨询方面的服务空白——在这些社区中，遗传咨询并不总是作为常规医疗服务的一部分。",
                },
                {
                  q: "咨询是保密的吗？我的信息存放在哪里？",
                  a:
                    "绝对保密。所有咨询均严格按照专业和法律隐私标准进行；未经您的明确同意，您的信息绝不会被分享。您的信息收集表、咨询记录，以及您提供给我们的任何报告，都存放在加拿大。",
                },
                {
                  q: "遗传咨询师可以为我开具基因检测吗？",
                  a:
                    "目前不能。GeneFlou 不开具基因检测。遗传咨询师能做的是：根据您的个人和家族病史，说明您可以选择哪些检测，并引导您完成整个过程，包括在您所在地区如何安排检测，以及可以为您开具检测的医疗人员是谁，通常是家庭医生或专科医生。之后我们会帮助您理解检测的内容以及结果的含义。",
                },
                {
                  q: "如果我已经有基因检测结果了怎么办？",
                  a:
                    "我们经常为已经收到基因筛查报告但需要帮助解读的个人提供服务。请在咨询之前把结果发送给我们，我们会为您详细解读其含义，讨论任何影响，并帮助您规划后续步骤。",
                },
              ],
            },
          },
          reproductiveRisk: {
            title: "生育风险评估",
            body:
              "我们会回顾您的个人和家族健康史，识别可能提示遗传性生育风险的规律；在可以计算的情况下，我们还会算出疾病被传下去的可能性。我们会说明家族史评估的局限、您是否符合所在地区公费检测的条件，以及携带者筛查、针对性检测、产前筛查、诊断性检测或其他专科评估是否可能提供更多信息。",
            details:
              "个人和家族健康史是遗传风险评估的重要组成部分。遗传咨询师可回顾家族双方的健康状况、诊断年龄、祖源、妊娠史及已知基因检测结果。这些信息可能显示提示遗传风险的规律，但不能完整反映您的 DNA，也无法识别所有遗传性疾病。\n\n系谱分析（家族树）\n在有帮助的情况下，我们会把现有信息整理成多代家族树，即系谱。系谱有助于解释可能的遗传方式，并显示哪些情况可能需要更具针对性的评估或检测。\n\n哪些人可能受益？\n此服务可能适合：\n• 家族中有已知或疑似遗传性疾病的个人或伴侣。\n• 有先天差异、智力障碍、早发疾病或同一疾病多次出现家族史的人士。\n• 已有基因检测结果，并希望了解结果对妊娠可能意味着什么的人士。\n• 正在计划怀孕，希望讨论携带者筛查或产前检测的人士。\n\n可能的后续步骤\n根据评估结果，后续步骤可能包括无需进一步检测、针对已知家族变异进行检测、携带者筛查、产前筛查或诊断性检测，或转介至其他专科。建议会因人而异，任何家族史评估都无法提供绝对确定性。",
            diagram: {
              title: "如何看懂系谱图",
              intro:
                "系谱是用一套标准符号绘制的家族树，任何遗传学专业人员都能以同样的方式解读。下面是我们与您一同绘制的图表示例，以及各个符号的含义。",
              legend: {
                male: "男性",
                female: "女性",
                carrier: "携带者",
                affected: "患病",
                unknown: "状态未知",
                consultand: "接受评估者",
              },
              reading:
                "解读这个例子：第 I 代中有一位祖辈是携带者。第 II 代中，他们的一个孩子患病，另一个是携带者。第 III 代中，前来接受评估的人尚未检测，因此其携带者状态仍然未知；其同胞的状态同样未知，以问号标示。",
              footnote:
                "以上是医学系谱图使用的通用符号。实际的系谱还会记录年龄、诊断年龄、祖源和妊娠史，其完整程度取决于可获得的家族信息。",
              description:
                "一幅三代系谱示例。方形代表男性，圆形代表女性；实心符号表示该人患病，中心圆点表示为携带者，问号表示状态未知。箭头标示接受评估者。第 I 代中一位祖母是携带者。第 II 代中一个儿子患病，一个女儿是携带者；她与配偶在第 III 代育有两个孩子，即接受评估者及其同胞；两人都尚未检测，因此都以问号标示。",
            },
            whatToExpect: {
              body:
                "生育风险评估会重点说明现有信息可能提示什么，以及这些信息有哪些局限。\n\n预约之前\n我们会请您填写一份家族健康史问卷，涵盖家族双方的健康状况。如果其中有些内容您不确定，值得向亲属打听。多数人发现，一问之下能得到的信息比想象中多。\n您收集到的信息越多，评估就越细致、越有用。最有帮助的细节是疾病名称、确诊年龄；如果亲属已经过世，还包括其年龄和死因。\n\n评估可能包括\n• 回顾您的个人和家族健康史\n• 根据您提供的信息绘制多代家族树（系谱）\n• 讨论可能提示遗传风险的规律\n• 在可以计算的情况下，算出疾病被传下去的可能性\n• 解释相关检测选项及每项检测能提供和不能提供的信息\n• 您的家族史或生育相关的可能性是否符合进一步基因检测的条件，包括现行指南的建议，以及您所在地区可获得哪些服务\n• 提供个性化建议和后续步骤\n\n预约之后\n您会收到一份可以留存的总结材料，其中包括您的系谱、评估发现的内容，以及建议的后续步骤。这份材料归您所有，您可以自行决定与谁分享，无论是可能同样相关的亲属，还是您自己的医疗服务提供者。\n\n评估的具体程度取决于现有信息的数量和质量。家族史有限或没有明显特征，并不能排除遗传风险；在某些情况下，基因检测可提供更多信息。",
            },
            process: {
              body:
                "第1步：初次咨询\n我们首先了解您的目标、家庭规划时间和具体关切，并说明家族史评估能提供和不能提供的信息。\n\n第2步：收集家族健康史\n我们会请您收集家族双方现有的健康信息。可能有帮助的资料包括疾病情况、诊断年龄、祖源、妊娠史及已知基因检测结果。我们会提供结构化表格协助您整理。\n\n第3步：系谱审阅\n在适当情况下，遗传咨询师会把资料整理成多代家族树（系谱），并审阅其中是否存在可能提示遗传风险的规律。\n\n第4步：风险评估与建议\n我们会解释现有信息可能意味着什么、仍有哪些不确定性，以及进一步评估或检测是否可能有帮助。\n\n第5步：审阅与后续步骤\n我们会用清晰语言审阅评估结果、回答您的问题，并讨论个性化后续步骤。在征得您同意后，相关信息可与家人或医疗服务提供者分享。",
            },
            faq: {
              items: [
                {
                  q: "生育风险评估对我有什么用？",
                  a:
                    "您是否想过，家族中某位成员的某种健康状况或特征是否会遗传？这项评估能让您更清楚地了解，孩子出现家族中已有疾病的可能性有多大。有些疾病既受基因影响，也受环境影响，及早了解可以让您有时间采取保护措施。此外，如果评估显示可能性增加，您或许符合公费检测的资格，具体取决于您所在地区的医疗项目。",
                },
                {
                  q: "什么是系谱（家族树），为什么它很重要？",
                  a:
                    "系谱是一份多代家族树，记录健康状况、诊断年龄、祖源、妊娠史和家庭关系。它可帮助遗传咨询师识别可能提示遗传风险的规律，但不能识别所有遗传性疾病，也不能在需要检测时取代基因检测。",
                },
                {
                  q: "我对家族病史了解不多，还能做评估吗？",
                  a:
                    "可以。虽然更完整的家族病史能提供更精确的评估，但即使信息有限，我们仍能提供有价值的见解。如果您的家族病史有限，我们还可以讨论是否通过基因检测来获取信息。",
                },
                {
                  q: "评估能告诉我是否一定会生育患有遗传病的孩子吗？",
                  a:
                    "没有任何评估能提供绝对的确定性。我们提供的是基于您家族病史的潜在生育风险综合评估。这有助于您了解是否需要进一步检测（如携带者筛查或PGT），并帮助您对家庭规划做出明智决定。",
                },
                {
                  q: "我已经怀孕了，这项评估还有用吗？",
                  a:
                    "有用。在受孕之前进行评估，您可以选择的方案最多，例如试管婴儿配合胚胎植入前遗传学检测（PGT），或使用捐赠的卵子、精子或胚胎，这些途径届时都还是开放的。怀孕之后评估依然有用。如果您已经怀孕，我们会说明家族史评估与携带者筛查、产前筛查之间如何配合，以及每一项能补充什么、不能补充什么。怀孕之后时间很重要：多项检测都有与孕周挂钩的时间窗，出结果也需要时间，因此宜早不宜迟。",
                },
                {
                  q: "我可以把评估结果分享给家人、医生或生育专家吗？",
                  a:
                    "可以，而且我们鼓励您这样做。您的总结材料归您所有，可以自行分享；把它传递出去，往往正是让信息触达真正需要的人的方式：与您有共同家族史的亲属、您的家庭医生，或可结合您的病史和其他临床信息作出判断的生育专家。分享什么、分享给谁，始终由您决定。",
                },
              ],
            },
          },
          carrierScreening: {
            title: "主动基因筛查",
            body:
              "面向希望及早了解的人。携带者筛查评估在怀孕前或怀孕期间将某种遗传性疾病传给孩子的可能性；遗传性癌症筛查则关注您自身的健康，寻找会提高某些癌症可能性的遗传变异。",
            details:
              "主动基因筛查是在您身体健康时主动选择的检测，而不是因症状或诊断而进行的检测。它能告诉您什么，取决于您选择哪一类，各类别详见下文。\n\n筛查评估的是可能性，而非确定性。没有任何筛查能覆盖所有基因或检出所有致病变异；结合个人和家族健康史一起解读，结果才最有意义。在您决定是否检测之前，我们会说明某项检测能提供和不能提供哪些信息。",
            diagram: {
              title: "这些疾病如何遗传",
              intro:
                "孩子的每个基因，一个拷贝来自卵子，一个拷贝来自精子。下面的图示说明：当一方或双方伴侣携带与同一种常染色体隐性遗传病相关的致病变异时，每次怀孕可能出现的结果。",
              legend: {
                working: "正常工作的拷贝",
                variant: "带有致病变异的拷贝",
              },
              autosomal: {
                heading: "常染色体隐性遗传病",
                note: "孩子需要两个基因拷贝都带有致病变异，才会患上这种疾病。",
              },
              dominant: {
                heading: "常染色体显性遗传病",
                note: "只需一个基因拷贝带有致病变异，这个人就会受影响，因此这类疾病通常在家族的每一代都会出现。",
              },
              dominantParent: {
                heading: "当父母其中一方患病时",
                parentA: "患病",
                parentB: "未患病",
                affected: "患病",
                unaffected: "未患病",
                note: "每次怀孕有 50% 的可能性生育患病孩子。未遗传到该变异的孩子不会再往下传。携带该变异并不一定会发病；即使发病，同一家族中的严重程度和发病年龄也可能不同。",
              },
              xlinked: {
                heading: "X 连锁疾病",
                note: "这些疾病与 X 染色体上的基因有关，因此结果取决于孩子遗传到哪些性染色体。下图中的 X 和 Y 即代表这两条性染色体。",
              },
              xlinkedCarrier: {
                heading: "当卵子提供者是携带者时",
                parentA: "携带者",
                parentB: "非携带者",
                twoXCarrier: "携带者",
                twoXNotCarrier: "非携带者",
                oneXAffected: "患病",
                oneXNotAffected: "未患病",
                note: "总体而言，每次怀孕有 25% 的可能性生育患病孩子。拥有两条 X 染色体的携带者中，有些也可能出现轻微或程度不一的症状，确切可能性取决于具体疾病。",
              },
              bothCarriers: {
                heading: "双方伴侣都是携带者",
                parentA: "携带者",
                parentB: "携带者",
                affected: "患病",
                carrier: "携带者",
                unaffected: "非携带者",
                note: "每次怀孕有 25% 的可能性生育患病孩子。",
              },
              oneCarrier: {
                heading: "只有一方伴侣是携带者",
                parentA: "携带者",
                parentB: "非携带者",
                carrier: "携带者",
                unaffected: "非携带者",
                note: "生育患该疾病孩子的可能性不会增加。这是完成携带者筛查的伴侣中最常见的结果。",
              },
              footnote:
                "每次怀孕的可能性都相同，不会因之前怀孕的结果而改变。",
              description:
                "三幅遗传图示。第一幅中双方伴侣都是同一种常染色体隐性遗传病的携带者：每次怀孕的四种可能结果中，一个孩子患病，两个是携带者，一个未遗传到任何一个家族变异——即 25% 的可能性生育患病孩子、50% 的可能性生育携带者、25% 的可能性两个变异都未遗传。第二幅中只有一方伴侣是携带者：四种可能结果中有两个是携带者，两个未遗传到变异——即 50% 的可能性生育携带者孩子，而生育患病孩子的可能性不会增加。第三幅显示卵子提供者为携带者的 X 连锁隐性遗传病：四种可能结果中，一个拥有两条 X 染色体的孩子是携带者，一个不是；一个拥有一条 X 和一条 Y 染色体的孩子患病，一个未患病——各占 25%。",
            },
            screening: {
              carrier: {
                heading: "携带者筛查",
                body:
                  "携带者筛查是一种自愿性基因检测，用于寻找与可能传给孩子的遗传性疾病相关的基因变异。检测可在怀孕前或怀孕期间进行，用于评估生育相关的可能性；它不能识别所有遗传性疾病，也无法预测所有可能的健康情况。扩展性检测组合通常包括囊性纤维化、脊髓性肌萎缩、脆性 X 综合征、镰状细胞病和血友病等疾病。\n\n常染色体隐性遗传病\n大多数基因有两个拷贝——一个来自卵子，一个来自精子。当其中一个基因拷贝带有致病变异时，这个人被视为携带者。由于另一个拷贝正常工作，携带者通常健康。\n\n如果双方伴侣都携带与同一种常染色体隐性遗传病相关的致病变异，每次怀孕有：\n• 25% 的可能性生育患病孩子\n• 50% 的可能性生育同样为携带者的孩子\n• 25% 的可能性生育未遗传到任何一个家族变异的孩子\n\n每次怀孕的可能性都相同。\n\n如果只有一方伴侣携带致病变异，生育患该疾病孩子的可能性不会增加。每次怀孕有 50% 的可能性生育同样为携带者的孩子，以及 50% 的可能性生育未遗传到任何一个家族变异的孩子。这也是完成携带者筛查的伴侣中最常见的结果。\n\nX 连锁疾病\n这些疾病与 X 染色体上的基因有关。只有一条 X 染色体的人，如果该 X 染色体带有致病变异，较容易患病。拥有两条 X 染色体的人可能是携带者，但部分携带者也可能出现轻微或程度不一的症状。对于 X 连锁隐性遗传病，如果卵子提供者是携带者，每次怀孕生育患病孩子的总体可能性为 25%；确切可能性取决于具体疾病及孩子遗传到的染色体。\n\n携带者很常见，而且多数携带者没有已知家族史。检测组合的规模、携带者检出比例和检出率会因实验室和人群而异。以涵盖 500 多种疾病的扩展性检测组合为例，实验室资料显示，约 70% 的受检者至少有一项携带者结果，约 4% 的受检伴侣或妊娠被发现生育相关的可能性增加，总体检出率约为 95%。这些数字仅适用于相应检测组合，并可能随时间改变。\n\n没有任何携带者筛查可以检出所有致病变异。阴性结果会降低——但不能完全排除——您是携带者的可能性；剩余的可能性称为残余风险。如果家族中有已知遗传性疾病，建议在检测前咨询遗传咨询师。",
                diagram: {
                  footnote: "每次怀孕的可能性都相同，不会因之前怀孕的结果而改变。",
                  description:
                    "三幅遗传图示。第一幅中双方伴侣都是同一种常染色体隐性遗传病的携带者：每次怀孕的四种可能结果中，一个孩子患病，两个是携带者，一个未遗传到任何一个家族变异——即 25% 的可能性生育患病孩子、50% 的可能性生育携带者、25% 的可能性两个变异都未遗传。第二幅中只有一方伴侣是携带者：四种可能结果中有两个是携带者，两个未遗传到变异——即 50% 的可能性生育携带者孩子，而生育患病孩子的可能性不会增加。第三幅显示卵子提供者为携带者的 X 连锁隐性遗传病：四种可能结果中，一个拥有两条 X 染色体的孩子是携带者，一个不是；一个拥有一条 X 和一条 Y 染色体的孩子患病，一个未患病——各占 25%。",
                },
              },
              hereditaryCancer: {
                heading: "遗传性癌症筛查",
                body:
                  "有些癌症之所以在家族中出现，是因为某个原本有保护作用的基因发生了可遗传的变异。筛查是在人仍然健康时寻找这些变异，使携带者可以更早或更频繁地开始监测、考虑降低可能性的方案，并告知可能携带同一变异的亲属。\n\n大多数癌症并非遗传而来，携带这类变异也不等于确诊。它描述的是一生中较高的可能性，而不是必然结果，有些携带者终身不会发病。当家族中存在某种规律时，筛查提供的信息最多，例如多位亲属罹患同一种癌症、在异常年轻时确诊，或出现已知会同时发生的癌症组合。家族史看起来没有异常，并不能排除存在可遗传的变异。\n\n可遗传的癌症易感性通常属于常染色体显性遗传。基因的两个拷贝中只要有一个带有致病变异，这个人的可能性就会升高，因此这类疾病往往在家族的多代中出现。携带该变异的人每次怀孕有 50% 的可能性将其传给孩子；未遗传到该变异的孩子不会再往下传。\n\n这也是同一个变异在不同亲属身上表现不同的原因。涉及哪些癌症、可能性有多高、通常在什么年龄出现，都会因基因和家族而异；仅凭携带该变异，并不足以预期任何特定结果。",
                diagram: {
                  footnote: "50% 针对每一次怀孕单独计算。遗传到该变异会提高一生中罹患某些癌症的可能性，但这并不是确诊。",
                  description: "一幅遗传图示。父母其中一方患有常染色体显性遗传病，带有一个变异拷贝和一个正常拷贝；另一方则有两个正常拷贝。每次怀孕的四种可能结果中，两个孩子遗传到变异拷贝并患病，两个遗传到两个正常拷贝而未患病，即两者各占 50%。",
                },
              },
            },
            faq: {
              items: [
                {
                  q: "什么是携带者？",
                  a:
                    "携带者通常是指某个基因的一个拷贝带有致病变异，而另一个拷贝正常工作的人。常染色体隐性遗传病的携带者通常健康。对于大多数这类疾病，双方伴侣都必须携带与同一种疾病相关的致病变异，生育患病孩子的可能性才会增加。",
                },
                {
                  q: "携带者常见吗？",
                  a:
                    "携带者很常见，而且多数携带者没有已知家族史。发现携带者结果的比例取决于检测组合包含的基因和疾病、实验室及受检人群。对于一种涵盖 500 多种疾病的扩展性检测组合，实验室报告约 70% 的受检者至少有一项携带者结果。",
                },
                {
                  q: "如果我是携带者，我的伴侣也需要检测吗？",
                  a:
                    "对于大多数常染色体隐性遗传病，双方伴侣都需要携带与同一种疾病相关的致病变异，生育患病孩子的可能性才会增加。因此，如果发现您是携带者，检测另一位生育伴侣通常是下一步。X 连锁疾病采用不同的遗传方式，因此伴侣检测不一定能回答相同的问题。",
                },
                {
                  q: "如果我是携带者，但我的伴侣不是，会怎样？",
                  a:
                    "对于常染色体隐性遗传病，如果只有一方生育伴侣携带致病变异，生育患该疾病孩子的可能性不会增加。每次怀孕有 50% 的可能性生育同样为携带者的孩子，以及 50% 的可能性生育未遗传到任何一个家族变异的孩子。这是完成携带者筛查的伴侣中最常见的结果。携带者通常健康；如果孩子是携带者，将来计划自己的家庭时可考虑接受筛查。此外，阴性结果仍然只是降低——而非完全排除——身为携带者的可能性。",
                },
                {
                  q: "携带者筛查能检测所有遗传疾病吗？",
                  a:
                    "不能。携带者筛查不包括所有遗传性疾病，也不能检出受检基因中的所有致病变异。阴性结果会降低——但不能排除——您是携带者的可能性。剩余的可能性称为残余风险，并会因疾病、实验室方法以及个人或家族史而异。",
                },
                {
                  q: "应该在怀孕前还是怀孕期间做携带者筛查？",
                  a:
                    "携带者筛查可在怀孕前或怀孕期间进行。在怀孕前做可以让您在发现风险增加时拥有最广泛的生育选择。不过，在怀孕期间做仍然有价值——它可以帮助您做好准备并指导产前检测的决定。",
                },
                {
                  q: "如果我和伴侣都是同一疾病的携带者，我有什么选择？",
                  a:
                    "对于常染色体隐性遗传病，如果双方伴侣都携带与同一种疾病相关的致病变异，每次怀孕通常有 25% 的可能性生育患病孩子。可选方案可能包括继续家庭计划、在适当情况下进行试管婴儿配合胚胎植入前遗传学检测（PGT）、使用捐赠卵子或精子、怀孕期间进行产前诊断性检测，或收养。我们会在支持性、非指导性的环境中讨论所有可选择的方案。",
                },
              ],
            },
          },
          hiddenScreening: {
            title: "扩展性携带者筛查",
            body: "在怀孕前或怀孕期间进行 DNA 检测，评估将某些遗传性疾病传给孩子的可能性。",
            details:
              "携带者筛查是一种自愿性基因检测，用于寻找与可能传给孩子的遗传性疾病相关的基因变异。检测可在怀孕前或怀孕期间进行，用于评估生育相关的可能性；它不能识别所有遗传性疾病，也无法预测所有可能的健康情况。扩展性检测组合通常包括囊性纤维化、脊髓性肌萎缩、脆性 X 综合征、镰状细胞病和血友病等疾病。\n\n常染色体隐性遗传病\n大多数基因有两个拷贝——一个来自卵子，一个来自精子。当其中一个基因拷贝带有致病变异时，这个人被视为携带者。由于另一个拷贝正常工作，携带者通常健康。\n\n如果双方伴侣都携带与同一种常染色体隐性遗传病相关的致病变异，每次怀孕有：\n• 25% 的可能性生育患病孩子\n• 50% 的可能性生育同样为携带者的孩子\n• 25% 的可能性生育未遗传到任何一个家族变异的孩子\n\n每次怀孕的可能性都相同。\n\n如果只有一方伴侣携带致病变异，生育患该疾病孩子的可能性不会增加。每次怀孕有 50% 的可能性生育同样为携带者的孩子，以及 50% 的可能性生育未遗传到任何一个家族变异的孩子。这也是完成携带者筛查的伴侣中最常见的结果。\n\nX 连锁疾病\n这些疾病与 X 染色体上的基因有关。只有一条 X 染色体的人，如果该 X 染色体带有致病变异，较容易患病。拥有两条 X 染色体的人可能是携带者，但部分携带者也可能出现轻微或程度不一的症状。对于 X 连锁隐性遗传病，如果卵子提供者是携带者，每次怀孕生育患病孩子的总体可能性为 25%；确切可能性取决于具体疾病及孩子遗传到的染色体。\n\n携带者很常见，而且多数携带者没有已知家族史。检测组合的规模、携带者检出比例和检出率会因实验室和人群而异。以涵盖 500 多种疾病的扩展性检测组合为例，实验室资料显示，约 70% 的受检者至少有一项携带者结果，约 4% 的受检伴侣或妊娠被发现生育相关的可能性增加，总体检出率约为 95%。这些数字仅适用于相应检测组合，并可能随时间改变。\n\n没有任何携带者筛查可以检出所有致病变异。阴性结果会降低——但不能完全排除——您是携带者的可能性；剩余的可能性称为残余风险。如果家族中有已知遗传性疾病，建议在检测前咨询遗传咨询师。",
            diagram: {
              title: "这些疾病如何遗传",
              intro:
                "孩子的每个基因，一个拷贝来自卵子，一个拷贝来自精子。下面的图示说明：当一方或双方伴侣携带与同一种常染色体隐性遗传病相关的致病变异时，每次怀孕可能出现的结果。",
              legend: {
                working: "正常工作的拷贝",
                variant: "带有致病变异的拷贝",
              },
              autosomal: {
                heading: "常染色体隐性遗传病",
                note: "孩子需要两个基因拷贝都带有致病变异，才会患上这种疾病。",
              },
              dominant: {
                heading: "常染色体显性遗传病",
                note: "只需一个基因拷贝带有致病变异，这个人就会受影响，因此这类疾病通常在家族的每一代都会出现。",
              },
              dominantParent: {
                heading: "当父母其中一方患病时",
                parentA: "患病",
                parentB: "未患病",
                affected: "患病",
                unaffected: "未患病",
                note: "每次怀孕有 50% 的可能性生育患病孩子。未遗传到该变异的孩子不会再往下传。携带该变异并不一定会发病；即使发病，同一家族中的严重程度和发病年龄也可能不同。",
              },
              xlinked: {
                heading: "X 连锁疾病",
                note: "这些疾病与 X 染色体上的基因有关，因此结果取决于孩子遗传到哪些性染色体。下图中的 X 和 Y 即代表这两条性染色体。",
              },
              xlinkedCarrier: {
                heading: "当卵子提供者是携带者时",
                parentA: "携带者",
                parentB: "非携带者",
                twoXCarrier: "携带者",
                twoXNotCarrier: "非携带者",
                oneXAffected: "患病",
                oneXNotAffected: "未患病",
                note: "总体而言，每次怀孕有 25% 的可能性生育患病孩子。拥有两条 X 染色体的携带者中，有些也可能出现轻微或程度不一的症状，确切可能性取决于具体疾病。",
              },
              bothCarriers: {
                heading: "双方伴侣都是携带者",
                parentA: "携带者",
                parentB: "携带者",
                affected: "患病",
                carrier: "携带者",
                unaffected: "非携带者",
                note: "每次怀孕有 25% 的可能性生育患病孩子。",
              },
              oneCarrier: {
                heading: "只有一方伴侣是携带者",
                parentA: "携带者",
                parentB: "非携带者",
                carrier: "携带者",
                unaffected: "非携带者",
                note: "生育患该疾病孩子的可能性不会增加。这是完成携带者筛查的伴侣中最常见的结果。",
              },
              footnote:
                "每次怀孕的可能性都相同，不会因之前怀孕的结果而改变。",
              description:
                "三幅遗传图示。第一幅中双方伴侣都是同一种常染色体隐性遗传病的携带者：每次怀孕的四种可能结果中，一个孩子患病，两个是携带者，一个未遗传到任何一个家族变异——即 25% 的可能性生育患病孩子、50% 的可能性生育携带者、25% 的可能性两个变异都未遗传。第二幅中只有一方伴侣是携带者：四种可能结果中有两个是携带者，两个未遗传到变异——即 50% 的可能性生育携带者孩子，而生育患病孩子的可能性不会增加。第三幅显示卵子提供者为携带者的 X 连锁隐性遗传病：四种可能结果中，一个拥有两条 X 染色体的孩子是携带者，一个不是；一个拥有一条 X 和一条 Y 染色体的孩子患病，一个未患病——各占 25%。",
            },
            whatToExpect: {
              body:
                "携带者筛查是一种可在怀孕前或怀孕期间进行的自愿性基因检测。\n\n检测如何进行\n• 血液或唾液样本会送往实验室进行 DNA 分析\n• 实验室收到样本后，结果通常可在 2–3 周内提供，但具体时间会有所不同\n• 遗传咨询师会说明检测能检出和不能检出的内容，并用清晰语言解释结果\n\n检测内容\n扩展性检测组合可包括数百种常染色体隐性和 X 连锁疾病。具体基因、疾病、携带者比例和检出率取决于所选实验室及检测组合。\n\n理解您的结果\n• 阴性结果会降低——但不能排除——您是携带者的可能性。剩余的可能性称为残余风险。\n• 阳性结果表示实验室发现了与特定疾病相关的致病或可能致病变异。生育相关的可能性取决于疾病、遗传方式及伴侣的检测结果。\n• 对于常染色体隐性遗传病，如果双方伴侣都携带与同一种疾病相关的致病变异，每次怀孕通常有 25% 的可能性生育患病孩子。\n• 如果只有一方伴侣携带与某种常染色体隐性遗传病相关的变异，生育患病孩子的可能性不会增加；每次怀孕有 50% 的可能性生育同样为携带者的孩子。\n• 如果发现生育相关的可能性增加，我们会在支持性、非指导性的环境中讨论可选择的方案。",
            },
            process: {
              body:
                "第1步：检测前咨询\n检测前，遗传咨询师会说明携带者筛查能检出和不能检出的内容，回顾您的个人及家族健康史，并协助您判断检测组合是否合适。如果家族中有已知遗传性疾病，仅进行常规筛查可能并不足够，针对性评估可能更合适。\n\n第2步：样本采集\n采集血液或唾液样本后，将样本送往所选实验室分析。\n\n第3步：实验室分析\n实验室分析所选检测组合包含的基因。检测规模和周转时间会有所不同；实验室收到样本后，结果通常可在 2–3 周内提供。\n\n第4步：结果解读\n遗传咨询师会解释检测结果及其局限。如果您携带与常染色体隐性遗传病相关的变异，检测另一位生育伴侣通常是下一步。我们会讨论疾病、遗传方式、残余风险，以及结果对家庭规划可能意味着什么。\n\n第5步：生育选择（如适用）\n如果结果显示生育患遗传性疾病孩子的可能性增加，可选方案可能包括：\n• 继续家庭计划，不作改变\n• 在适当情况下进行试管婴儿（IVF）配合胚胎植入前遗传学检测（PGT）\n• 使用捐赠的卵子、精子或胚胎\n• 怀孕期间进行产前诊断性检测\n• 收养\n\n我们会在支持性、非指导性的环境中讨论所有可选择的方案。",
            },
            faq: {
              items: [
                {
                  q: "什么是携带者？",
                  a:
                    "携带者通常是指某个基因的一个拷贝带有致病变异，而另一个拷贝正常工作的人。常染色体隐性遗传病的携带者通常健康。对于大多数这类疾病，双方伴侣都必须携带与同一种疾病相关的致病变异，生育患病孩子的可能性才会增加。",
                },
                {
                  q: "携带者常见吗？",
                  a:
                    "携带者很常见，而且多数携带者没有已知家族史。发现携带者结果的比例取决于检测组合包含的基因和疾病、实验室及受检人群。对于一种涵盖 500 多种疾病的扩展性检测组合，实验室报告约 70% 的受检者至少有一项携带者结果。",
                },
                {
                  q: "如果我是携带者，我的伴侣也需要检测吗？",
                  a:
                    "对于大多数常染色体隐性遗传病，双方伴侣都需要携带与同一种疾病相关的致病变异，生育患病孩子的可能性才会增加。因此，如果发现您是携带者，检测另一位生育伴侣通常是下一步。X 连锁疾病采用不同的遗传方式，因此伴侣检测不一定能回答相同的问题。",
                },
                {
                  q: "如果我是携带者，但我的伴侣不是，会怎样？",
                  a:
                    "对于常染色体隐性遗传病，如果只有一方生育伴侣携带致病变异，生育患该疾病孩子的可能性不会增加。每次怀孕有 50% 的可能性生育同样为携带者的孩子，以及 50% 的可能性生育未遗传到任何一个家族变异的孩子。这是完成携带者筛查的伴侣中最常见的结果。携带者通常健康；如果孩子是携带者，将来计划自己的家庭时可考虑接受筛查。此外，阴性结果仍然只是降低——而非完全排除——身为携带者的可能性。",
                },
                {
                  q: "携带者筛查能检测所有遗传疾病吗？",
                  a:
                    "不能。携带者筛查不包括所有遗传性疾病，也不能检出受检基因中的所有致病变异。阴性结果会降低——但不能排除——您是携带者的可能性。剩余的可能性称为残余风险，并会因疾病、实验室方法以及个人或家族史而异。",
                },
                {
                  q: "应该在怀孕前还是怀孕期间做携带者筛查？",
                  a:
                    "携带者筛查可在怀孕前或怀孕期间进行。在怀孕前做可以让您在发现风险增加时拥有最广泛的生育选择。不过，在怀孕期间做仍然有价值——它可以帮助您做好准备并指导产前检测的决定。",
                },
                {
                  q: "如果我和伴侣都是同一疾病的携带者，我有什么选择？",
                  a:
                    "对于常染色体隐性遗传病，如果双方伴侣都携带与同一种疾病相关的致病变异，每次怀孕通常有 25% 的可能性生育患病孩子。可选方案可能包括继续家庭计划、在适当情况下进行试管婴儿配合胚胎植入前遗传学检测（PGT）、使用捐赠卵子或精子、怀孕期间进行产前诊断性检测，或收养。我们会在支持性、非指导性的环境中讨论所有可选择的方案。",
                },
              ],
            },
          },
          prenatalScreening: {
            title: "产前筛查",
            body:
              "产前筛查其实是好几种不同的检测，您能获得哪几种，取决于您所在的地区。我们会说明每一种检测查看什么、能告诉您什么、不能告诉您什么，以及在您所在地哪些项目由公费承担，帮助您决定做哪些、不做哪些。",
            details:
              "产前筛查用于评估妊娠存在某些染色体疾病的可能性。携带者筛查寻找生育伴侣基因中的变异，而产前筛查关注妊娠本身。筛查的内容取决于所选项目：有些查看染色体，有一项还筛查开放性神经管缺陷，超声检查则关注妊娠的发育情况。\n\n哪些项目可以选择、哪些由公费承担，会因省份和国家而异。下面列出的是通常会提供的项目，而不是完整清单：当地的医疗项目可能有不同的组合方式，或使用自己的名称。我们会说明您所在地区可获得哪些项目、每一项能检出和不能检出什么，以及您有权选择不做其中任何一项。\n\n筛查与诊断性检测\n筛查用于估计可能性，不能确认是否存在疾病。绒毛膜取样（CVS）或羊膜穿刺术等诊断性检测，可以确定所检测的特定染色体疾病是否存在。我们会说明每个选项的益处、局限和可能风险，包括选择不检测。\n\n解读您的结果\n高可能性或非典型筛查结果并不能确认妊娠存在疾病。我们会解释结果、结合具体情况估算阳性预测值（PPV），并讨论遗传咨询、详细超声检查以及通过 CVS 或羊膜穿刺术进行诊断性检测。低可能性结果会降低所筛查疾病的可能性，但不能将其降至零。",
            diagram: {
              title: "NIPT 究竟在检测什么",
              intro:
                "NIPT 只需采集孕妇的一份血液样本。样本中含有许多游离 DNA 片段，而这些片段并非都来自同一处。",
              legend: {
                maternal: "来自孕妇的游离 DNA",
                placental: "来自胎盘的游离 DNA",
              },
              sampleLabel: "一份血液样本",
              scaleNote: "图示比例仅供示意——胎盘来源的比例因人而异。",
              notesArray: [
                "样本中大部分游离 DNA 来自孕妇本人，而非妊娠本身。",
                "只有一小部分来自胎盘。筛查正是依靠这部分 DNA，因此 NIPT 反映的是胎盘情况，而非直接反映胎儿。",
                "当来自胎盘的比例过低时，实验室可能无法得出结果。这种情况约占 1–3%，体重指数较高时更为常见。",
              ],
              footnote:
                "由于 NIPT 检测的是胎盘的 DNA，结果偶尔可能与胎儿自身的染色体不一致。这也是可能性增加的结果需要通过诊断性检测确认的原因之一。",
              description:
                "一幅血液样本图示，样本中含有许多游离 DNA 短片段。大部分片段来自孕妇；只有分散其中的一小部分来自胎盘。NIPT 依靠的正是这一小部分来自胎盘的 DNA，因此它反映的是胎盘情况而非直接反映胎儿；当来自胎盘的比例过低时（约占 1–3%，体重指数较高时更为常见），实验室可能无法得出结果。",
            },
            screening: {
              nipt: {
                heading: "无创产前筛查（NIPT）",
                body:
                  "NIPT 又称游离 DNA 筛查，分析孕妇血液中循环的游离 DNA 片段。这些片段大部分来自孕妇本人，只有一小部分来自胎盘；检测正是利用这部分来自胎盘的 DNA 来评估染色体疾病的可能性。它是筛查常见三体综合征最敏感、特异性最高的方法，但仍可能出现假阳性、假阴性或无结果。NIPT 不能筛查所有染色体或结构异常，因此超声检查及其他建议的产前护理仍然重要。\n\nNIPT 通常筛查什么\n• 唐氏综合征（21-三体）\n• 爱德华氏综合征（18-三体）\n• 帕陶氏综合征（13-三体）\n• 三倍体（每条染色体都多出一个拷贝），部分检测组合包含\n• 经选择作为可选附加项目的性染色体异常\n\n部分实验室也提供特定微缺失综合征筛查。这些疾病很罕见，高可能性结果有较高机会属于假阳性，因此不建议对一般孕妇常规进行微缺失筛查。考虑此选项的人应接受检测前咨询，并了解必须通过诊断性检测确认结果。\n\nNIPT 是一种血液筛查，通常可从怀孕 10 周起进行。对于 21-三体，部分经过验证的检测报告检出率超过 99%、假阳性率低于 0.1%；实际表现会因疾病、实验室和临床情况而异。实验室收到样本后，结果通常可在 5–10 天内提供，具体时间视实验室而定。\n\n约 1–3% 的情况下，实验室无法得出结果，最常见的原因是样本中来自胎盘的 DNA 过少；体重指数较高时更容易出现这种情况。通常会安排重新抽血，部分实验室也可选择退款。无结果本身并不属于异常结果，但在决定后续步骤前值得先作讨论。",
                diagram: {
                  footnote: "由于 NIPT 检测的是胎盘的 DNA，结果偶尔可能与胎儿自身的染色体不一致。这也是可能性增加的结果需要通过诊断性检测确认的原因之一。",
                  description:
                    "一幅血液样本图示，样本中含有许多游离 DNA 短片段。大部分片段来自孕妇；只有分散其中的一小部分来自胎盘。NIPT 依靠的正是这一小部分来自胎盘的 DNA，因此它反映的是胎盘情况而非直接反映胎儿；当来自胎盘的比例过低时（约占 1–3%，体重指数较高时更为常见），实验室可能无法得出结果。",
                },
              },
              fts: {
                heading: "早孕期筛查（FTS）",
                body:
                  "早孕期筛查将胎儿颈部后方的一项超声测量（称为颈项透明层）与孕妇血液中的标志物结合起来。检查通常在怀孕约 11 至 14 周之间进行，用于评估唐氏综合征和爱德华氏综合征的可能性。与 NIPT 相比，它能识别的受累妊娠较少，假阳性结果也较多；但它普及程度高，在许多地区可获得公费资助，而 NIPT 未必如此。",
              },
              serum: {
                heading: "母血清筛查",
                body:
                  "血清筛查检测孕妇血液中的蛋白质和激素，通常在怀孕约 15 至 20 周之间进行。它评估唐氏综合征和爱德华氏综合征的可能性；与 NIPT 不同，它还能筛查脊柱裂等开放性神经管缺陷。部分项目会把早孕期和中孕期两次采血合并为一个结果，其表现优于单独任何一次。",
              },
              ultrasound: {
                heading: "超声检查",
                body:
                  "超声检查关注的是妊娠的发育情况，而非染色体。早期核对孕周的扫描可确认怀孕的周数；颈项透明层测量在约 11 至 14 周之间进行；详细的胎儿结构扫描通常在约 18 至 22 周之间提供。超声可以发现任何血液检测都无法检出的结构差异，也可以发现改变染色体疾病可能性的特征。但仅凭超声无法确认或排除某种遗传性疾病。",
              },
            },
            faq: {
              items: [
                {
                  q: "筛查和诊断性检测有什么区别？",
                  a:
                    "筛查用于估计某种疾病的可能性，不能确认疾病是否存在。通过 CVS 或羊膜穿刺术进行诊断性检测，可以确定所检测的特定疾病是否存在。这些操作伴有较小的操作相关流产风险，风险会因操作和医疗中心而异；您的产前护理医生应向您说明适用于您的估计值。",
                },
                {
                  q: "NIPT 准确吗？",
                  a:
                    "NIPT 是筛查常见三体综合征最敏感、特异性最高的方法。对于 21-三体，部分经过验证的检测报告检出率超过 99%、假阳性率低于 0.1%，但实际表现会因疾病、实验室和临床情况而异。NIPT 不是诊断性检测，仍可能出现假阳性、假阴性或无结果；可能性增加的结果之后应接受遗传咨询，并获得诊断性检测选项。",
                },
                {
                  q: "什么时候可以做产前筛查？",
                  a:
                    "取决于您选择哪一项。NIPT 通常可从怀孕 10 周起进行，实验室收到样本后，结果通常可在 5–10 天内提供。早孕期筛查在约 11 至 14 周之间进行，因为它需要结合血液检测和只能在这一时间窗内测量的颈项透明层。母血清筛查通常在约 15 至 20 周之间进行。超声检查则贯穿其中：早期有核对孕周的扫描，颈项透明层测量在约 11 至 14 周之间，详细的胎儿结构扫描通常在约 18 至 22 周之间提供。诊断性检测有自己的时间窗：绒毛膜取样通常在约 11 至 14 周之间提供，羊膜穿刺术则从约 15 周起。一旦错过某个时间窗，部分选项就会关闭，因此与其自己猜测，不如尽早询问。",
                },
                {
                  q: "如果 NIPT 得不出结果怎么办？",
                  a:
                    "约 1–3% 的情况下，实验室无法得出结果，最常见的原因是样本中来自胎盘的 DNA 过少；体重指数较高时更容易出现这种情况。通常会安排重新抽血，部分实验室也可选择退款。无结果本身并不属于异常结果，但建议与遗传咨询师或产前护理医生讨论，由他们评估重新抽血、超声检查，或改用其他筛查或诊断性检测是否更适合您的情况。",
                },
                {
                  q: "NIPT 能检测所有问题吗？",
                  a:
                    "不能。NIPT 主要筛查 21-三体、18-三体和 13-三体。性染色体筛查可在接受适当咨询后作为自选项目。部分实验室提供特定微缺失筛查，但由于这些罕见疾病的检测表现有限，不建议对一般孕妇常规进行微缺失筛查。NIPT 不能评估所有染色体疾病、单基因疾病或结构异常，因此建议的超声检查和产前护理仍然重要。",
                },
                {
                  q: "如果收到高风险结果怎么办？",
                  a:
                    "可能性增加的结果并不能确认妊娠存在疾病。我们会解释结果及其局限，结合具体情况估算阳性预测值（PPV），并讨论遗传咨询、详细超声检查以及通过 CVS 或羊膜穿刺术进行诊断性检测。是否进行进一步检测由您决定。",
                },
                {
                  q: "我在试管婴儿期间做了 PGT-A，还需要产前筛查吗？",
                  a:
                    "PGT-A 会降低但不能排除染色体异常的可能性，也无法识别所有遗传性或结构性疾病。完成 PGT-A 后，仍应讨论产前筛查和诊断性检测。最合适的选项取决于您的具体情况、目标和偏好，建议的产前超声检查仍然重要。",
                },
              ],
            },
          },
          hiddenPrenatal: {
            title: "产前筛查",
            body:
              "所有怀孕人士——无论年龄或初始可能性——都应获得关于产前筛查和诊断性检测的清晰信息，包括选择不检测的权利。GeneFlou 会解释您所在地区可选择的方案，并根据您的价值观、情况和偏好支持共同决策。",
            details:
              "产前筛查用于评估妊娠存在某些染色体疾病的可能性。携带者筛查寻找生育伴侣基因中的变异，而产前筛查关注妊娠本身。常见筛查疾病包括唐氏综合征（21-三体）、爱德华氏综合征（18-三体）和帕陶氏综合征（13-三体）。\n\n筛查与诊断性检测\n筛查用于估计可能性，不能确认是否存在疾病。绒毛膜取样（CVS）或羊膜穿刺术等诊断性检测，可以确定所检测的特定染色体疾病是否存在。我们会说明每个选项的益处、局限和可能风险，包括选择不检测。\n\n无创产前筛查（NIPT）\nNIPT 又称游离 DNA 筛查，分析孕妇血液中循环的游离 DNA 片段。这些片段大部分来自孕妇本人，只有一小部分来自胎盘；检测正是利用这部分来自胎盘的 DNA 来评估染色体疾病的可能性。它是筛查常见三体综合征最敏感、特异性最高的方法，但仍可能出现假阳性、假阴性或无结果。NIPT 不能筛查所有染色体或结构异常，因此超声检查及其他建议的产前护理仍然重要。\n\n解读您的结果\n高可能性或非典型筛查结果并不能确认妊娠存在疾病。我们会解释结果、结合具体情况估算阳性预测值（PPV），并讨论遗传咨询、详细超声检查以及通过 CVS 或羊膜穿刺术进行诊断性检测。低可能性结果会降低所筛查疾病的可能性，但不能将其降至零。",
            diagram: {
              title: "NIPT 究竟在检测什么",
              intro:
                "NIPT 只需采集孕妇的一份血液样本。样本中含有许多游离 DNA 片段，而这些片段并非都来自同一处。",
              legend: {
                maternal: "来自孕妇的游离 DNA",
                placental: "来自胎盘的游离 DNA",
              },
              sampleLabel: "一份血液样本",
              scaleNote: "图示比例仅供示意——胎盘来源的比例因人而异。",
              notesArray: [
                "样本中大部分游离 DNA 来自孕妇本人，而非妊娠本身。",
                "只有一小部分来自胎盘。筛查正是依靠这部分 DNA，因此 NIPT 反映的是胎盘情况，而非直接反映胎儿。",
                "当来自胎盘的比例过低时，实验室可能无法得出结果。这种情况约占 1–3%，体重指数较高时更为常见。",
              ],
              footnote:
                "由于 NIPT 检测的是胎盘的 DNA，结果偶尔可能与胎儿自身的染色体不一致。这也是可能性增加的结果需要通过诊断性检测确认的原因之一。",
              description:
                "一幅血液样本图示，样本中含有许多游离 DNA 短片段。大部分片段来自孕妇；只有分散其中的一小部分来自胎盘。NIPT 依靠的正是这一小部分来自胎盘的 DNA，因此它反映的是胎盘情况而非直接反映胎儿；当来自胎盘的比例过低时（约占 1–3%，体重指数较高时更为常见），实验室可能无法得出结果。",
            },
            whatToExpect: {
              body:
                "产前筛查用于评估妊娠存在某些染色体疾病的可能性。\n\nNIPT 通常筛查什么\n• 唐氏综合征（21-三体）\n• 爱德华氏综合征（18-三体）\n• 帕陶氏综合征（13-三体）\n• 三倍体（每条染色体都多出一个拷贝），部分检测组合包含\n• 经选择作为可选附加项目的性染色体异常\n\n部分实验室也提供特定微缺失综合征筛查。这些疾病很罕见，高可能性结果有较高机会属于假阳性，因此不建议对一般孕妇常规进行微缺失筛查。考虑此选项的人应接受检测前咨询，并了解必须通过诊断性检测确认结果。\n\n无创产前筛查（NIPT）\nNIPT 是一种血液筛查，通常可从怀孕 10 周起进行。对于 21-三体，部分经过验证的检测报告检出率超过 99%、假阳性率低于 0.1%；实际表现会因疾病、实验室和临床情况而异。实验室收到样本后，结果通常可在 5–10 天内提供，具体时间视实验室而定。\n\n约 1–3% 的情况下，实验室无法得出结果，最常见的原因是样本中来自胎盘的 DNA 过少；体重指数较高时更容易出现这种情况。通常会安排重新抽血，部分实验室也可选择退款。无结果本身并不属于异常结果，但在决定后续步骤前值得先作讨论。\n\n筛查与诊断性检测\n筛查用于估计可能性，不能提供诊断。如果结果显示可能性增加，应讨论遗传咨询、详细超声检查以及通过 CVS 或羊膜穿刺术进行诊断性检测。",
            },
            process: {
              body:
                "第1步：筛查前咨询\n遗传咨询师会解释您所在地区可选择的产前筛查和诊断性检测、每项检测能检出和不能检出的内容，以及选择不检测的权利。\n\n第2步：选择方案\n根据当地可及性和您的偏好，选项可能包括 NIPT（游离 DNA 筛查）、早孕期筛查（FTS）、其他母体血清筛查或诊断性检测。性染色体筛查属于需要检测前咨询的自选项目。不建议对一般孕妇常规进行微缺失筛查。\n\n第3步：样本采集\nNIPT 使用血液样本，通常可从怀孕 10 周起进行。FTS 结合血液检测和超声检查，一般在 11–14 周进行。时间和流程取决于所选检测及当地医疗系统。\n\n第4步：结果与解读\n实验室收到样本后，NIPT 结果通常可在 5–10 天内提供；FTS 的时间则因项目而异。低可能性结果会降低但不能排除所筛查疾病的可能性。对于高可能性、非典型或无结果的情况，我们会解释局限，并在适用时估算 PPV 和讨论适当的后续步骤。\n\n第5步：诊断性检测（如适用）\n筛查结果显示可能性增加或无法得出结论后，可能会提供 CVS 或羊膜穿刺术等诊断性检测。我们会解释每项操作可以检测什么、适用时间、局限及较小的操作相关风险，帮助您作出知情选择。",
            },
            faq: {
              items: [
                {
                  q: "筛查和诊断性检测有什么区别？",
                  a:
                    "筛查用于估计某种疾病的可能性，不能确认疾病是否存在。通过 CVS 或羊膜穿刺术进行诊断性检测，可以确定所检测的特定疾病是否存在。这些操作伴有较小的操作相关流产风险，风险会因操作和医疗中心而异；您的产前护理医生应向您说明适用于您的估计值。",
                },
                {
                  q: "NIPT 准确吗？",
                  a:
                    "NIPT 是筛查常见三体综合征最敏感、特异性最高的方法。对于 21-三体，部分经过验证的检测报告检出率超过 99%、假阳性率低于 0.1%，但实际表现会因疾病、实验室和临床情况而异。NIPT 不是诊断性检测，仍可能出现假阳性、假阴性或无结果；可能性增加的结果之后应接受遗传咨询，并获得诊断性检测选项。",
                },
                {
                  q: "什么时候可以做 NIPT？",
                  a:
                    "NIPT 通常可从怀孕 10 周起进行，需要采集孕妇的血液样本。实验室收到样本后，结果通常可在 5–10 天内提供，但具体时间会有所不同。",
                },
                {
                  q: "如果 NIPT 得不出结果怎么办？",
                  a:
                    "约 1–3% 的情况下，实验室无法得出结果，最常见的原因是样本中来自胎盘的 DNA 过少；体重指数较高时更容易出现这种情况。通常会安排重新抽血，部分实验室也可选择退款。无结果本身并不属于异常结果，但建议与遗传咨询师或产前护理医生讨论，由他们评估重新抽血、超声检查，或改用其他筛查或诊断性检测是否更适合您的情况。",
                },
                {
                  q: "NIPT 能检测所有问题吗？",
                  a:
                    "不能。NIPT 主要筛查 21-三体、18-三体和 13-三体。性染色体筛查可在接受适当咨询后作为自选项目。部分实验室提供特定微缺失筛查，但由于这些罕见疾病的检测表现有限，不建议对一般孕妇常规进行微缺失筛查。NIPT 不能评估所有染色体疾病、单基因疾病或结构异常，因此建议的超声检查和产前护理仍然重要。",
                },
                {
                  q: "如果收到高风险结果怎么办？",
                  a:
                    "可能性增加的结果并不能确认妊娠存在疾病。我们会解释结果及其局限，结合具体情况估算阳性预测值（PPV），并讨论遗传咨询、详细超声检查以及通过 CVS 或羊膜穿刺术进行诊断性检测。是否进行进一步检测由您决定。",
                },
                {
                  q: "我在试管婴儿期间做了 PGT-A，还需要产前筛查吗？",
                  a:
                    "PGT-A 会降低但不能排除染色体异常的可能性，也无法识别所有遗传性或结构性疾病。完成 PGT-A 后，仍应讨论产前筛查和诊断性检测。最合适的选项取决于您的具体情况、目标和偏好，建议的产前超声检查仍然重要。",
                },
              ],
            },
          },
          learnMore: "了解更多 →",
        },
      },
      team: {
        title: "GeneFlou 团队",
        olive: {
          name: "Olive Zhu",
          role: "联合创始人及遗传咨询师",
          bio:
            "Olive 是一名加拿大认证的遗传咨询师，同时担任英属哥伦比亚大学医学遗传学系的临床讲师。她的专长涵盖备孕基因检测与产前遗传学，并曾在加拿大、英国、美国及中国等地工作，对亚洲地区不断发展的遗传咨询服务格局尤为关注。",
        },
        chris: {
          name: "Christopher Chan",
          role: "联合创始人",
          bio:
            "Chris 与 Olive 共同创立了 GeneFlou，负责机构的网站、日常运营、对外推广与发展。作为华人，他深知当家庭能以母语交流时，这类对话会顺畅得多；他对 GeneFlou 的期望，是让细致、高质量的遗传咨询服务在粤语和普通话中，与英语一样触手可及。",
        },
      },
      contact: {
        title: "保持联系",
        description:
          "您可以直接发送邮件至 genetics@geneflou.com，或填写下方表格与我们联系！",
        form: {
          nameLabel: "姓名",
          namePlaceholder: "姓名",
          emailLabel: "电子邮箱",
          emailPlaceholder: "电子邮箱",
          messageLabel: "留言",
          messagePlaceholder: "请在此输入您的留言…",
          submit: "发送信息",
        },
      },
      language: {
        label: "语言",
        selector: "选择语言",
        english: "English",
        simplified: "简体中文",
        traditional: "繁體中文",
        shortEnglish: "EN",
        shortSimplified: "简体",
        shortTraditional: "繁體",
      },
      gate: {
        title: "此页面尚未公开",
        prompt: "此页面仍在制作中。请输入密码继续，或返回主网站。",
        label: "密码",
        submit: "继续",
        error: "密码不正确，请重试。",
      },
      nav: {
        home: "首页",
        services: "服务",
        resources: "资源",
        backToTop: "返回顶部",
      },
      resources: {
        hero: {
          title: "资源",
          lede: "当问题超出一次咨询所能涵盖的范围时，我们会推荐这些资源——如何找到遗传咨询师、卑诗省提供哪些服务，以及可以在哪里进一步了解。",
        },
        opensInNewTab: "在新标签页中打开",
        group: {
          findCounsellor: {
            title: "寻找遗传咨询师",
            entry: {
              cbgc: "查询加拿大注册遗传咨询师名录。CBGC 是加拿大遗传咨询师的认证机构。",
              nsgc: "收录加拿大与美国超过 3,300 位遗传咨询师，可选择面诊或远程咨询。这是一份名录，并非转诊服务。",
              bcWomens:
                "为卑诗省及育空地区（温哥华岛除外）提供服务的省级遗传学门诊。就诊需由医疗服务提供者转介。",
            },
          },
          prenatalBc: {
            title: "卑诗省的产前筛查",
            entry: {
              perinatalServicesBc:
                "介绍本省提供哪些产前基因筛查，以及如何在各项选择之间作出决定。持有 MSP 保险者免费，并且始终由您自行决定是否接受。",
              gecKoPrenatalGuide:
                "以通俗易懂的语言说明产前筛查检测哪些情况，以及一份结果意味着什么、不意味着什么。",
              gecKoNipt:
                "介绍无创产前检测的原理、可以筛查哪些情况，以及为什么阳性结果需要进一步确诊。",
            },
          },
          understandingGenetics: {
            title: "了解遗传学",
            entry: {
              medlinePlus:
                "美国国家医学图书馆编写的指南，涵盖 1,300 多种疾病，并设有面向零基础读者的遗传学入门专栏。",
              gecKoBasics:
                "从基因、染色体和遗传方式讲起，专为没有生物学基础的读者撰写。",
              gard: "美国国立卫生研究院建立的罕见病与遗传病数据库，并有资讯专员直接解答问题。",
              gecKoGnda:
                "说明加拿大法律对谁可以索取您的基因检测结果的规定，其中包括保险公司与雇主。",
            },
          },
          livingWithCondition: {
            title: "与遗传疾病共处",
            entry: {
              rareDiseaseFoundation:
                "总部设于温哥华，提供病友互助与心理健康支持，也包括尚未确诊的家庭。",
              cord: "加拿大罕见病组织的全国网络，可从中找到针对特定疾病的支持团体。",
              unique:
                "提供各类染色体与基因疾病的免费下载指南，并有多种语言版本。机构位于英国，服务世界各地的家庭。",
            },
          },
        },
        disclaimer: {
          title: "关于这些链接",
          body: "这些均为独立机构。GeneFlou 无法控制其发布的内容，今天准确的页面日后也可能有所变动。此处链接的任何内容都不能替代针对您自身健康、怀孕或家族史的咨询——若您读到的内容引发疑问，欢迎向我们或您的医疗服务提供者提出。",
        },
      },
    },
  },
  "zh-Hant": {
    translation: {
      header: {
        welcome: "歡迎來到",
        tagline: "助力家庭茁壯成長。",
        cta: "預約諮詢",
      },
      landing: {
        servicesTitle: "我們的服務",
        servicesDescription:
          "GeneFlou 是一間遺傳諮詢服務機構，致力協助成長中的家庭了解其基因檢測選項，並掌握相關的生育風險。我們透過提供粵語、普通話及英語服務，期望填補華人社群在遺傳諮詢方面的服務缺口。",
        haveQuestions: "有疑問？",
        contactPrompt: "聯絡我們以了解更多關於我們的服務及我們如何為您提供協助。",
        getInTouch: "聯絡我們",
        serviceSection: {
          overview: { title: "服務概覽" },
          whatToExpect: { title: "服務流程與預期" },
          process: { title: "服務流程" },
          faq: { title: "常見問題" },
          onThisPage: "本頁內容",
        },

        service: {
          geneticCounselling: {
            title: "遺傳諮詢",
            body:
              "遺傳諮詢是協助人們理解並適應遺傳因素對健康和疾病所帶來的醫學、心理及家庭影響的過程。在 GeneFlou，我們提供資訊與支援，協助個人和家庭就基因檢測及自身健康作出知情決定。",
            details:
              "遺傳諮詢師是接受過醫學遺傳學和諮詢專業教育的醫療專業人員。他們會回顧個人和家族健康史，協助人們理解遺傳風險，解釋基因檢測能提供和不能提供的資訊，並討論檢測結果在醫學和情緒方面的影響。\n\nGeneFlou 提供英語、普通話和粵語個人化諮詢，重點涵蓋生殖健康、產前篩檢和遺傳性疾病。我們的目標是提供清晰資訊和支持性、非指導性的諮詢，讓您的決定符合自身需要、價值觀和家庭情況。",
            whenToReachOut: "何時聯絡我們",
            whenToReachOutSubtitle: "如果您符合以下情況，GeneFlou 的諮詢服務可能會對您特別有幫助：",
            whenToReachOutBulletsArray: [
              "收到了基因篩檢報告，需要幫助解讀結果。",
              "懷疑存在遺傳疾病或對家族健康史有擔憂。",
              "正在考慮備孕或產前檢測，想了解相關選項。",
              "需要能用普通話或粵語流利提供的專業護理和指導。",
              "是一位積極規劃的人，相信為意外做好準備，希望在人生新篇章開始之前對自己的遺傳健康有清晰、全面的了解。",
            ],
            whatToExpect: {
              body:
                "我們的諮詢旨在為您提供一次支持性、無批判的對話，根據您的獨特情況和關切量身訂製。\n\n諮詢過程中會發生什麼\n• 全面回顧您個人和家族的病史\n• 用通俗易懂的語言解釋遺傳學概念\n• 討論可用的基因檢測選項及其能告訴您什麼、不能告訴您什麼\n• 在您考慮各種選項時提供情感支持和指導\n• 提供個人化的總結和建議及後續步驟\n\n諮詢通常為 30–60 分鐘，以線上形式進行，支援英語、普通話或粵語。歡迎伴侶或家人一同參加。沒有問題是微不足道的，我們鼓勵您帶著任何疑問前來。",
            },
            process: {
              body:
                "第1步：聯絡我們\n請透過 genetics@geneflou.com 或我們的網站聯絡我們表達您的意向。我們會回覆您，安排您的諮詢，並收集您可以提供的初步資訊。\n\n第2步：諮詢前資訊收集\n在預約之前，我們會向您發送一份簡短的表格，用於收集您的個人和家族病史。這讓我們能夠提前準備，充分利用您的諮詢時間。\n\n第3步：諮詢\n在諮詢過程中，您的遺傳諮詢師將回顧您的病史，討論您的關切，解釋相關的遺傳學概念，並概述您的檢測選項和生育風險。這是一次雙向對話——您的價值觀和偏好將引導討論方向。\n\n第4步：基因檢測（如適用）\n如果您決定進行基因檢測，我們會說明在您所在地區如何安排檢測，並為您指出可以開立檢測的醫療人員，例如家庭醫生或專科醫生。我們會幫助您了解檢測的內容和預期結果時間。\n\n第5步：結果與後續跟進\n結果出來後，您的遺傳諮詢師將用清晰、富有同理心的語言為您解讀。我們會討論對您和家人的影響，探索您的選項，並支持您對後續步驟做出明智決定。您還會收到一份詳細的書面諮詢摘要，其中包含相關建議。這份摘要歸您所有，您可以自行決定與誰分享，無論是可能同樣相關的親屬，還是您自己的醫療服務提供者。",
            },
            faq: {
              items: [
                {
                  q: "什麼是遺傳諮詢師？",
                  a:
                    "遺傳諮詢師是接受過醫學遺傳學和諮詢專業訓練的醫療專業人員。他們會回顧個人和家族健康史，解釋遺傳風險和檢測選項，解讀結果，並在人們作出知情決定時提供支援。GeneFlou 的遺傳諮詢服務由一位獲加拿大認證的遺傳諮詢師提供。",
                },
                {
                  q: "我需要轉介才能預約諮詢嗎？",
                  a:
                    "不需要轉介。您可以直接透過 genetics@geneflou.com 聯絡我們預約。不過，如果您有之前的基因檢測結果或病歷，我們可能會請您提前提供，以便我們提供最有針對性的指導。",
                },
                {
                  q: "諮詢提供哪些語言？",
                  a:
                    "我們提供英語、普通話和粵語諮詢。我們創立 GeneFlou 的初衷正是為了填補華人社群在遺傳諮詢方面的服務缺口——在這些社群中，遺傳諮詢並不總是作為常規醫療服務的一部分。",
                },
                {
                  q: "諮詢是保密的嗎？我的資訊存放在哪裡？",
                  a:
                    "絕對保密。所有諮詢均嚴格按照專業和法律隱私標準進行；未經您的明確同意，您的資訊絕不會被分享。您的資訊收集表、諮詢記錄，以及您提供給我們的任何報告，都存放在加拿大。",
                },
                {
                  q: "遺傳諮詢師可以為我開立基因檢測嗎？",
                  a:
                    "目前不能。GeneFlou 不開立基因檢測。遺傳諮詢師能做的是：根據您的個人和家族病史，說明您可以選擇哪些檢測，並引導您完成整個過程，包括在您所在地區如何安排檢測，以及可以為您開立檢測的醫療人員是誰，通常是家庭醫生或專科醫生。之後我們會幫助您理解檢測的內容以及結果的含義。",
                },
                {
                  q: "如果我已經有基因檢測結果了怎麼辦？",
                  a:
                    "我們經常為已經收到基因篩檢報告但需要幫助解讀的個人提供服務。請在諮詢之前把結果發送給我們，我們會為您詳細解讀其含義，討論任何影響，並幫助您規劃後續步驟。",
                },
              ],
            },
          },
          reproductiveRisk: {
            title: "生育風險評估",
            body:
              "我們會回顧您的個人和家族健康史，識別可能提示遺傳性生育風險的規律；在可以計算的情況下，我們還會算出疾病被傳下去的可能性。我們會說明家族史評估的局限、您是否符合所在地區公費檢測的條件，以及攜帶者篩檢、針對性檢測、產前篩檢、診斷性檢測或其他專科評估是否可能提供更多資訊。",
            details:
              "個人和家族健康史是遺傳風險評估的重要部分。遺傳諮詢師可回顧家族雙方的健康狀況、診斷年齡、祖源、懷孕史及已知基因檢測結果。這些資訊可能顯示提示遺傳風險的規律，但不能完整反映您的 DNA，也無法識別所有遺傳性疾病。\n\n系譜分析（家族樹）\n在有幫助的情況下，我們會把現有資訊整理成多代家族樹，即系譜。系譜有助於解釋可能的遺傳方式，並顯示哪些情況可能需要更具針對性的評估或檢測。\n\n哪些人可能受益？\n此服務可能適合：\n• 家族中有已知或疑似遺傳性疾病的個人或伴侶。\n• 有先天差異、智力障礙、早發疾病或同一疾病多次出現家族史的人士。\n• 已有基因檢測結果，並希望了解結果對懷孕可能意味著什麼的人士。\n• 正在計劃懷孕，希望討論攜帶者篩檢或產前檢測的人士。\n\n可能的後續步驟\n根據評估結果，後續步驟可能包括無需進一步檢測、針對已知家族變異進行檢測、攜帶者篩檢、產前篩檢或診斷性檢測，或轉介至其他專科。建議會因人而異，任何家族史評估都無法提供絕對確定性。",
            diagram: {
              title: "如何看懂系譜圖",
              intro:
                "系譜是用一套標準符號繪製的家族樹，任何遺傳學專業人員都能以同樣的方式解讀。下面是我們與您一同繪製的圖表示例，以及各個符號的含義。",
              legend: {
                male: "男性",
                female: "女性",
                carrier: "攜帶者",
                affected: "患病",
                unknown: "狀態未知",
                consultand: "接受評估者",
              },
              reading:
                "解讀這個例子：第 I 代中有一位祖輩是攜帶者。第 II 代中，他們的一個孩子患病，另一個是攜帶者。第 III 代中，前來接受評估的人尚未檢測，因此其攜帶者狀態仍然未知；其同胞的狀態同樣未知，以問號標示。",
              footnote:
                "以上是醫學系譜圖使用的通用符號。實際的系譜還會記錄年齡、診斷年齡、祖源和懷孕史，其完整程度取決於可獲得的家族資訊。",
              description:
                "一幅三代系譜示例。方形代表男性，圓形代表女性；實心符號表示該人患病，中心圓點表示為攜帶者，問號表示狀態未知。箭頭標示接受評估者。第 I 代中一位祖母是攜帶者。第 II 代中一個兒子患病，一個女兒是攜帶者；她與配偶在第 III 代育有兩個孩子，即接受評估者及其同胞；兩人都尚未檢測，因此都以問號標示。",
            },
            whatToExpect: {
              body:
                "生育風險評估會重點說明現有資訊可能提示什麼，以及這些資訊有哪些局限。\n\n預約之前\n我們會請您填寫一份家族健康史問卷，涵蓋家族雙方的健康狀況。如果其中有些內容您不確定，值得向親屬打聽。多數人發現，一問之下能得到的資訊比想像中多。\n您收集到的資訊越多，評估就越細緻、越有用。最有幫助的細節是疾病名稱、確診年齡；如果親屬已經過世，還包括其年齡和死因。\n\n評估可能包括\n• 回顧您的個人和家族健康史\n• 根據您提供的資訊繪製多代家族樹（系譜）\n• 討論可能提示遺傳風險的規律\n• 在可以計算的情況下，算出疾病被傳下去的可能性\n• 解釋相關檢測選項及每項檢測能提供和不能提供的資訊\n• 您的家族史或生育相關的可能性是否符合進一步基因檢測的條件，包括現行指引的建議，以及您所在地區可獲得哪些服務\n• 提供個人化建議和後續步驟\n\n預約之後\n您會收到一份可以留存的總結材料，其中包括您的系譜、評估發現的內容，以及建議的後續步驟。這份材料歸您所有，您可以自行決定與誰分享，無論是可能同樣相關的親屬，還是您自己的醫療服務提供者。\n\n評估的具體程度取決於現有資訊的數量和質素。家族史有限或沒有明顯特徵，並不能排除遺傳風險；在某些情況下，基因檢測可提供更多資訊。",
            },
            process: {
              body:
                "第1步：初次諮詢\n我們首先了解您的目標、家庭規劃時間和具體關切，並說明家族史評估能提供和不能提供的資訊。\n\n第2步：收集家族健康史\n我們會請您收集家族雙方現有的健康資訊。可能有幫助的資料包括疾病情況、診斷年齡、祖源、懷孕史及已知基因檢測結果。我們會提供結構化表格協助您整理。\n\n第3步：系譜審閱\n在適當情況下，遺傳諮詢師會把資料整理成多代家族樹（系譜），並審閱其中是否存在可能提示遺傳風險的規律。\n\n第4步：風險評估與建議\n我們會解釋現有資訊可能意味著什麼、仍有哪些不確定性，以及進一步評估或檢測是否可能有幫助。\n\n第5步：審閱與後續步驟\n我們會用清晰語言審閱評估結果、回答您的問題，並討論個人化後續步驟。在徵得您同意後，相關資訊可與家人或醫療服務提供者分享。",
            },
            faq: {
              items: [
                {
                  q: "生育風險評估對我有什麼用？",
                  a:
                    "您是否想過，家族中某位成員的某種健康狀況或特徵是否會遺傳？這項評估能讓您更清楚地了解，孩子出現家族中已有疾病的可能性有多大。有些疾病既受基因影響，也受環境影響，及早了解可以讓您有時間採取保護措施。此外，如果評估顯示可能性增加，您或許符合公費檢測的資格，具體取決於您所在地區的醫療項目。",
                },
                {
                  q: "什麼是系譜（家族樹），為什麼它很重要？",
                  a:
                    "系譜是一份多代家族樹，記錄健康狀況、診斷年齡、祖源、懷孕史和家庭關係。它可協助遺傳諮詢師識別可能提示遺傳風險的規律，但不能識別所有遺傳性疾病，也不能在需要檢測時取代基因檢測。",
                },
                {
                  q: "我對家族病史了解不多，還能做評估嗎？",
                  a:
                    "可以。雖然更完整的家族病史能提供更精確的評估，但即使資訊有限，我們仍能提供有價值的見解。如果您的家族病史有限，我們還可以討論是否透過基因檢測來獲取資訊。",
                },
                {
                  q: "評估能告訴我是否一定會生育患有遺傳病的孩子嗎？",
                  a:
                    "沒有任何評估能提供絕對的確定性。我們提供的是基於您家族病史的潛在生育風險綜合評估。這有助於您了解是否需要進一步檢測（如攜帶者篩檢或PGT），並幫助您對家庭規劃做出明智決定。",
                },
                {
                  q: "我已經懷孕了，這項評估還有用嗎？",
                  a:
                    "有用。在受孕之前進行評估，您可以選擇的方案最多，例如試管嬰兒配合胚胎植入前遺傳學檢測（PGT），或使用捐贈的卵子、精子或胚胎，這些途徑屆時都還是開放的。懷孕之後評估依然有用。如果您已經懷孕，我們會說明家族史評估與攜帶者篩檢、產前篩檢之間如何配合，以及每一項能補充什麼、不能補充什麼。懷孕之後時間很重要：多項檢測都有與孕週掛鉤的時間窗，出結果也需要時間，因此宜早不宜遲。",
                },
                {
                  q: "我可以把評估結果分享給家人、醫生或生育專家嗎？",
                  a:
                    "可以，而且我們鼓勵您這樣做。您的總結材料歸您所有，可以自行分享；把它傳遞出去，往往正是讓資訊觸達真正需要的人的方式：與您有共同家族史的親屬、您的家庭醫生，或可結合您的病史和其他臨床資訊作出判斷的生育專家。分享什麼、分享給誰，始終由您決定。",
                },
              ],
            },
          },
          carrierScreening: {
            title: "主動基因篩檢",
            body:
              "面向希望及早了解的人。攜帶者篩檢評估在懷孕前或懷孕期間將某種遺傳性疾病傳給孩子的可能性；遺傳性癌症篩檢則關注您自身的健康，尋找會提高某些癌症可能性的遺傳變異。",
            details:
              "主動基因篩檢是在您身體健康時主動選擇的檢測，而不是因症狀或診斷而進行的檢測。它能告訴您什麼，取決於您選擇哪一類，各類別詳見下文。\n\n篩檢評估的是可能性，而非確定性。沒有任何篩檢能涵蓋所有基因或檢出所有致病變異；結合個人和家族健康史一起解讀，結果才最有意義。在您決定是否檢測之前，我們會說明某項檢測能提供和不能提供哪些資訊。",
            diagram: {
              title: "這些疾病如何遺傳",
              intro:
                "孩子的每個基因，一個拷貝來自卵子，一個拷貝來自精子。下面的圖示說明：當一方或雙方伴侶攜帶與同一種常染色體隱性遺傳病相關的致病變異時，每次懷孕可能出現的結果。",
              legend: {
                working: "正常運作的拷貝",
                variant: "帶有致病變異的拷貝",
              },
              autosomal: {
                heading: "常染色體隱性遺傳病",
                note: "孩子需要兩個基因拷貝都帶有致病變異，才會患上這種疾病。",
              },
              dominant: {
                heading: "常染色體顯性遺傳病",
                note: "只需一個基因拷貝帶有致病變異，這個人就會受影響，因此這類疾病通常在家族的每一代都會出現。",
              },
              dominantParent: {
                heading: "當父母其中一方患病時",
                parentA: "患病",
                parentB: "未患病",
                affected: "患病",
                unaffected: "未患病",
                note: "每次懷孕有 50% 的可能性生育患病孩子。未遺傳到該變異的孩子不會再往下傳。攜帶該變異並不一定會發病；即使發病，同一家族中的嚴重程度和發病年齡也可能不同。",
              },
              xlinked: {
                heading: "X 連鎖疾病",
                note: "這些疾病與 X 染色體上的基因有關，因此結果取決於孩子遺傳到哪些性染色體。下圖中的 X 和 Y 即代表這兩條性染色體。",
              },
              xlinkedCarrier: {
                heading: "當卵子提供者是攜帶者時",
                parentA: "攜帶者",
                parentB: "非攜帶者",
                twoXCarrier: "攜帶者",
                twoXNotCarrier: "非攜帶者",
                oneXAffected: "患病",
                oneXNotAffected: "未患病",
                note: "總體而言，每次懷孕有 25% 的可能性生育患病孩子。擁有兩條 X 染色體的攜帶者中，有些也可能出現輕微或程度不一的症狀，確切可能性取決於具體疾病。",
              },
              bothCarriers: {
                heading: "雙方伴侶都是攜帶者",
                parentA: "攜帶者",
                parentB: "攜帶者",
                affected: "患病",
                carrier: "攜帶者",
                unaffected: "非攜帶者",
                note: "每次懷孕有 25% 的可能性生育患病孩子。",
              },
              oneCarrier: {
                heading: "只有一方伴侶是攜帶者",
                parentA: "攜帶者",
                parentB: "非攜帶者",
                carrier: "攜帶者",
                unaffected: "非攜帶者",
                note: "生育患該疾病孩子的可能性不會增加。這是完成攜帶者篩檢的伴侶中最常見的結果。",
              },
              footnote:
                "每次懷孕的可能性都相同，不會因之前懷孕的結果而改變。",
              description:
                "三幅遺傳圖示。第一幅中雙方伴侶都是同一種常染色體隱性遺傳病的攜帶者：每次懷孕的四種可能結果中，一個孩子患病，兩個是攜帶者，一個未遺傳到任何一個家族變異——即 25% 的可能性生育患病孩子、50% 的可能性生育攜帶者、25% 的可能性兩個變異都未遺傳。第二幅中只有一方伴侶是攜帶者：四種可能結果中有兩個是攜帶者，兩個未遺傳到變異——即 50% 的可能性生育攜帶者孩子，而生育患病孩子的可能性不會增加。第三幅顯示卵子提供者為攜帶者的 X 連鎖隱性遺傳病：四種可能結果中，一個擁有兩條 X 染色體的孩子是攜帶者，一個不是；一個擁有一條 X 和一條 Y 染色體的孩子患病，一個未患病——各佔 25%。",
            },
            screening: {
              carrier: {
                heading: "攜帶者篩檢",
                body:
                  "攜帶者篩檢是一種自願性基因檢測，用於尋找與可能傳給孩子的遺傳性疾病相關的基因變異。檢測可在懷孕前或懷孕期間進行，用於評估生育相關的可能性；它不能識別所有遺傳性疾病，也無法預測所有可能的健康情況。擴展性檢測組合通常包括囊性纖維化、脊髓性肌萎縮、脆性 X 綜合症、鐮狀細胞病和血友病等疾病。\n\n常染色體隱性遺傳病\n大多數基因有兩個拷貝——一個來自卵子，一個來自精子。當其中一個基因拷貝帶有致病變異時，這個人被視為攜帶者。由於另一個拷貝正常運作，攜帶者通常健康。\n\n如果雙方伴侶都攜帶與同一種常染色體隱性遺傳病相關的致病變異，每次懷孕有：\n• 25% 的可能性生育患病孩子\n• 50% 的可能性生育同樣為攜帶者的孩子\n• 25% 的可能性生育未遺傳到任何一個家族變異的孩子\n\n每次懷孕的可能性都相同。\n\n如果只有一方伴侶攜帶致病變異，生育患該疾病孩子的可能性不會增加。每次懷孕有 50% 的可能性生育同樣為攜帶者的孩子，以及 50% 的可能性生育未遺傳到任何一個家族變異的孩子。這也是完成攜帶者篩檢的伴侶中最常見的結果。\n\nX 連鎖疾病\n這些疾病與 X 染色體上的基因有關。只有一條 X 染色體的人，如果該 X 染色體帶有致病變異，較容易患病。擁有兩條 X 染色體的人可能是攜帶者，但部分攜帶者也可能出現輕微或程度不一的症狀。對於 X 連鎖隱性遺傳病，如果卵子提供者是攜帶者，每次懷孕生育患病孩子的總體可能性為 25%；確切可能性取決於具體疾病及孩子遺傳到的染色體。\n\n攜帶者很常見，而且多數攜帶者沒有已知家族史。檢測組合的規模、攜帶者檢出比例和檢出率會因實驗室和人群而異。以涵蓋 500 多種疾病的擴展性檢測組合為例，實驗室資料顯示，約 70% 的受檢者至少有一項攜帶者結果，約 4% 的受檢伴侶或懷孕被發現生育相關的可能性增加，總體檢出率約為 95%。這些數字僅適用於相應檢測組合，並可能隨時間改變。\n\n沒有任何攜帶者篩檢可以檢出所有致病變異。陰性結果會降低——但不能完全排除——您是攜帶者的可能性；剩餘的可能性稱為殘餘風險。如果家族中有已知遺傳性疾病，建議在檢測前諮詢遺傳諮詢師。",
                diagram: {
                  footnote: "每次懷孕的可能性都相同，不會因之前懷孕的結果而改變。",
                  description:
                    "三幅遺傳圖示。第一幅中雙方伴侶都是同一種常染色體隱性遺傳病的攜帶者：每次懷孕的四種可能結果中，一個孩子患病，兩個是攜帶者，一個未遺傳到任何一個家族變異——即 25% 的可能性生育患病孩子、50% 的可能性生育攜帶者、25% 的可能性兩個變異都未遺傳。第二幅中只有一方伴侶是攜帶者：四種可能結果中有兩個是攜帶者，兩個未遺傳到變異——即 50% 的可能性生育攜帶者孩子，而生育患病孩子的可能性不會增加。第三幅顯示卵子提供者為攜帶者的 X 連鎖隱性遺傳病：四種可能結果中，一個擁有兩條 X 染色體的孩子是攜帶者，一個不是；一個擁有一條 X 和一條 Y 染色體的孩子患病，一個未患病——各佔 25%。",
                },
              },
              hereditaryCancer: {
                heading: "遺傳性癌症篩檢",
                body:
                  "有些癌症之所以在家族中出現，是因為某個原本有保護作用的基因發生了可遺傳的變異。篩檢是在人仍然健康時尋找這些變異，使攜帶者可以更早或更頻繁地開始監測、考慮降低可能性的方案，並告知可能攜帶同一變異的親屬。\n\n大多數癌症並非遺傳而來，攜帶這類變異也不等於確診。它描述的是一生中較高的可能性，而不是必然結果，有些攜帶者終身不會發病。當家族中存在某種規律時，篩檢提供的資訊最多，例如多位親屬罹患同一種癌症、在異常年輕時確診，或出現已知會同時發生的癌症組合。家族史看起來沒有異常，並不能排除存在可遺傳的變異。\n\n可遺傳的癌症易感性通常屬於常染色體顯性遺傳。基因的兩個拷貝中只要有一個帶有致病變異，這個人的可能性就會升高，因此這類疾病往往在家族的多代中出現。攜帶該變異的人每次懷孕有 50% 的可能性將其傳給孩子；未遺傳到該變異的孩子不會再往下傳。\n\n這也是同一個變異在不同親屬身上表現不同的原因。涉及哪些癌症、可能性有多高、通常在什麼年齡出現，都會因基因和家族而異；僅憑攜帶該變異，並不足以預期任何特定結果。",
                diagram: {
                  footnote: "50% 針對每一次懷孕單獨計算。遺傳到該變異會提高一生中罹患某些癌症的可能性，但這並不是確診。",
                  description: "一幅遺傳圖示。父母其中一方患有常染色體顯性遺傳病，帶有一個變異拷貝和一個正常拷貝；另一方則有兩個正常拷貝。每次懷孕的四種可能結果中，兩個孩子遺傳到變異拷貝並患病，兩個遺傳到兩個正常拷貝而未患病，即兩者各佔 50%。",
                },
              },
            },
            faq: {
              items: [
                {
                  q: "什麼是攜帶者？",
                  a:
                    "攜帶者通常是指某個基因的一個拷貝帶有致病變異，而另一個拷貝正常運作的人。常染色體隱性遺傳病的攜帶者通常健康。對於大多數這類疾病，雙方伴侶都必須攜帶與同一種疾病相關的致病變異，生育患病孩子的可能性才會增加。",
                },
                {
                  q: "攜帶者常見嗎？",
                  a:
                    "攜帶者很常見，而且多數攜帶者沒有已知家族史。發現攜帶者結果的比例取決於檢測組合包含的基因和疾病、實驗室及受檢人群。對於一種涵蓋 500 多種疾病的擴展性檢測組合，實驗室報告約 70% 的受檢者至少有一項攜帶者結果。",
                },
                {
                  q: "如果我是攜帶者，我的伴侶也需要檢測嗎？",
                  a:
                    "對於大多數常染色體隱性遺傳病，雙方伴侶都需要攜帶與同一種疾病相關的致病變異，生育患病孩子的可能性才會增加。因此，如果發現您是攜帶者，檢測另一位生育伴侶通常是下一步。X 連鎖疾病採用不同的遺傳方式，因此伴侶檢測不一定能回答相同的問題。",
                },
                {
                  q: "如果我是攜帶者，但我的伴侶不是，會怎樣？",
                  a:
                    "對於常染色體隱性遺傳病，如果只有一方生育伴侶攜帶致病變異，生育患該疾病孩子的可能性不會增加。每次懷孕有 50% 的可能性生育同樣為攜帶者的孩子，以及 50% 的可能性生育未遺傳到任何一個家族變異的孩子。這是完成攜帶者篩檢的伴侶中最常見的結果。攜帶者通常健康；如果孩子是攜帶者，將來計劃自己的家庭時可考慮接受篩檢。此外，陰性結果仍然只是降低——而非完全排除——身為攜帶者的可能性。",
                },
                {
                  q: "攜帶者篩檢能檢測所有遺傳疾病嗎？",
                  a:
                    "不能。攜帶者篩檢不包括所有遺傳性疾病，也不能檢出受檢基因中的所有致病變異。陰性結果會降低——但不能排除——您是攜帶者的可能性。剩餘的可能性稱為殘餘風險，並會因疾病、實驗室方法以及個人或家族史而異。",
                },
                {
                  q: "應該在懷孕前還是懷孕期間做攜帶者篩檢？",
                  a:
                    "攜帶者篩檢可在懷孕前或懷孕期間進行。在懷孕前做可以讓您在發現風險增加時擁有最廣泛的生育選擇。不過，在懷孕期間做仍然有價值——它可以幫助您做好準備並指導產前檢測的決定。",
                },
                {
                  q: "如果我和伴侶都是同一疾病的攜帶者，我有什麼選擇？",
                  a:
                    "對於常染色體隱性遺傳病，如果雙方伴侶都攜帶與同一種疾病相關的致病變異，每次懷孕通常有 25% 的可能性生育患病孩子。可選方案可能包括繼續家庭計劃、在適當情況下進行試管嬰兒配合胚胎植入前遺傳學檢測（PGT）、使用捐贈卵子或精子、懷孕期間進行產前診斷性檢測，或收養。我們會在支持性、非指導性的環境中討論所有可選擇的方案。",
                },
              ],
            },
          },
          hiddenScreening: {
            title: "擴展性攜帶者篩檢",
            body: "在懷孕前或懷孕期間進行 DNA 檢測，評估將某些遺傳性疾病傳給孩子的可能性。",
            details:
              "攜帶者篩檢是一種自願性基因檢測，用於尋找與可能傳給孩子的遺傳性疾病相關的基因變異。檢測可在懷孕前或懷孕期間進行，用於評估生育相關的可能性；它不能識別所有遺傳性疾病，也無法預測所有可能的健康情況。擴展性檢測組合通常包括囊性纖維化、脊髓性肌萎縮、脆性 X 綜合症、鐮狀細胞病和血友病等疾病。\n\n常染色體隱性遺傳病\n大多數基因有兩個拷貝——一個來自卵子，一個來自精子。當其中一個基因拷貝帶有致病變異時，這個人被視為攜帶者。由於另一個拷貝正常運作，攜帶者通常健康。\n\n如果雙方伴侶都攜帶與同一種常染色體隱性遺傳病相關的致病變異，每次懷孕有：\n• 25% 的可能性生育患病孩子\n• 50% 的可能性生育同樣為攜帶者的孩子\n• 25% 的可能性生育未遺傳到任何一個家族變異的孩子\n\n每次懷孕的可能性都相同。\n\n如果只有一方伴侶攜帶致病變異，生育患該疾病孩子的可能性不會增加。每次懷孕有 50% 的可能性生育同樣為攜帶者的孩子，以及 50% 的可能性生育未遺傳到任何一個家族變異的孩子。這也是完成攜帶者篩檢的伴侶中最常見的結果。\n\nX 連鎖疾病\n這些疾病與 X 染色體上的基因有關。只有一條 X 染色體的人，如果該 X 染色體帶有致病變異，較容易患病。擁有兩條 X 染色體的人可能是攜帶者，但部分攜帶者也可能出現輕微或程度不一的症狀。對於 X 連鎖隱性遺傳病，如果卵子提供者是攜帶者，每次懷孕生育患病孩子的總體可能性為 25%；確切可能性取決於具體疾病及孩子遺傳到的染色體。\n\n攜帶者很常見，而且多數攜帶者沒有已知家族史。檢測組合的規模、攜帶者檢出比例和檢出率會因實驗室和人群而異。以涵蓋 500 多種疾病的擴展性檢測組合為例，實驗室資料顯示，約 70% 的受檢者至少有一項攜帶者結果，約 4% 的受檢伴侶或懷孕被發現生育相關的可能性增加，總體檢出率約為 95%。這些數字僅適用於相應檢測組合，並可能隨時間改變。\n\n沒有任何攜帶者篩檢可以檢出所有致病變異。陰性結果會降低——但不能完全排除——您是攜帶者的可能性；剩餘的可能性稱為殘餘風險。如果家族中有已知遺傳性疾病，建議在檢測前諮詢遺傳諮詢師。",
            diagram: {
              title: "這些疾病如何遺傳",
              intro:
                "孩子的每個基因，一個拷貝來自卵子，一個拷貝來自精子。下面的圖示說明：當一方或雙方伴侶攜帶與同一種常染色體隱性遺傳病相關的致病變異時，每次懷孕可能出現的結果。",
              legend: {
                working: "正常運作的拷貝",
                variant: "帶有致病變異的拷貝",
              },
              autosomal: {
                heading: "常染色體隱性遺傳病",
                note: "孩子需要兩個基因拷貝都帶有致病變異，才會患上這種疾病。",
              },
              dominant: {
                heading: "常染色體顯性遺傳病",
                note: "只需一個基因拷貝帶有致病變異，這個人就會受影響，因此這類疾病通常在家族的每一代都會出現。",
              },
              dominantParent: {
                heading: "當父母其中一方患病時",
                parentA: "患病",
                parentB: "未患病",
                affected: "患病",
                unaffected: "未患病",
                note: "每次懷孕有 50% 的可能性生育患病孩子。未遺傳到該變異的孩子不會再往下傳。攜帶該變異並不一定會發病；即使發病，同一家族中的嚴重程度和發病年齡也可能不同。",
              },
              xlinked: {
                heading: "X 連鎖疾病",
                note: "這些疾病與 X 染色體上的基因有關，因此結果取決於孩子遺傳到哪些性染色體。下圖中的 X 和 Y 即代表這兩條性染色體。",
              },
              xlinkedCarrier: {
                heading: "當卵子提供者是攜帶者時",
                parentA: "攜帶者",
                parentB: "非攜帶者",
                twoXCarrier: "攜帶者",
                twoXNotCarrier: "非攜帶者",
                oneXAffected: "患病",
                oneXNotAffected: "未患病",
                note: "總體而言，每次懷孕有 25% 的可能性生育患病孩子。擁有兩條 X 染色體的攜帶者中，有些也可能出現輕微或程度不一的症狀，確切可能性取決於具體疾病。",
              },
              bothCarriers: {
                heading: "雙方伴侶都是攜帶者",
                parentA: "攜帶者",
                parentB: "攜帶者",
                affected: "患病",
                carrier: "攜帶者",
                unaffected: "非攜帶者",
                note: "每次懷孕有 25% 的可能性生育患病孩子。",
              },
              oneCarrier: {
                heading: "只有一方伴侶是攜帶者",
                parentA: "攜帶者",
                parentB: "非攜帶者",
                carrier: "攜帶者",
                unaffected: "非攜帶者",
                note: "生育患該疾病孩子的可能性不會增加。這是完成攜帶者篩檢的伴侶中最常見的結果。",
              },
              footnote:
                "每次懷孕的可能性都相同，不會因之前懷孕的結果而改變。",
              description:
                "三幅遺傳圖示。第一幅中雙方伴侶都是同一種常染色體隱性遺傳病的攜帶者：每次懷孕的四種可能結果中，一個孩子患病，兩個是攜帶者，一個未遺傳到任何一個家族變異——即 25% 的可能性生育患病孩子、50% 的可能性生育攜帶者、25% 的可能性兩個變異都未遺傳。第二幅中只有一方伴侶是攜帶者：四種可能結果中有兩個是攜帶者，兩個未遺傳到變異——即 50% 的可能性生育攜帶者孩子，而生育患病孩子的可能性不會增加。第三幅顯示卵子提供者為攜帶者的 X 連鎖隱性遺傳病：四種可能結果中，一個擁有兩條 X 染色體的孩子是攜帶者，一個不是；一個擁有一條 X 和一條 Y 染色體的孩子患病，一個未患病——各佔 25%。",
            },
            whatToExpect: {
              body:
                "攜帶者篩檢是一種可在懷孕前或懷孕期間進行的自願性基因檢測。\n\n檢測如何進行\n• 血液或唾液樣本會送往實驗室進行 DNA 分析\n• 實驗室收到樣本後，結果通常可在 2–3 週內提供，但具體時間會有所不同\n• 遺傳諮詢師會說明檢測能檢出和不能檢出的內容，並用清晰語言解釋結果\n\n檢測內容\n擴展性檢測組合可包括數百種常染色體隱性和 X 連鎖疾病。具體基因、疾病、攜帶者比例和檢出率取決於所選實驗室及檢測組合。\n\n理解您的結果\n• 陰性結果會降低——但不能排除——您是攜帶者的可能性。剩餘的可能性稱為殘餘風險。\n• 陽性結果表示實驗室發現了與特定疾病相關的致病或可能致病變異。生育相關的可能性取決於疾病、遺傳方式及伴侶的檢測結果。\n• 對於常染色體隱性遺傳病，如果雙方伴侶都攜帶與同一種疾病相關的致病變異，每次懷孕通常有 25% 的可能性生育患病孩子。\n• 如果只有一方伴侶攜帶與某種常染色體隱性遺傳病相關的變異，生育患病孩子的可能性不會增加；每次懷孕有 50% 的可能性生育同樣為攜帶者的孩子。\n• 如果發現生育相關的可能性增加，我們會在支持性、非指導性的環境中討論可選擇的方案。",
            },
            process: {
              body:
                "第1步：檢測前諮詢\n檢測前，遺傳諮詢師會說明攜帶者篩檢能檢出和不能檢出的內容，回顧您的個人及家族健康史，並協助您判斷檢測組合是否合適。如果家族中有已知遺傳性疾病，僅進行常規篩檢可能並不足夠，針對性評估可能更合適。\n\n第2步：樣本採集\n採集血液或唾液樣本後，將樣本送往所選實驗室分析。\n\n第3步：實驗室分析\n實驗室分析所選檢測組合包含的基因。檢測規模和週轉時間會有所不同；實驗室收到樣本後，結果通常可在 2–3 週內提供。\n\n第4步：結果解讀\n遺傳諮詢師會解釋檢測結果及其局限。如果您攜帶與常染色體隱性遺傳病相關的變異，檢測另一位生育伴侶通常是下一步。我們會討論疾病、遺傳方式、殘餘風險，以及結果對家庭規劃可能意味著什麼。\n\n第5步：生育選擇（如適用）\n如果結果顯示生育患遺傳性疾病孩子的可能性增加，可選方案可能包括：\n• 繼續家庭計劃，不作改變\n• 在適當情況下進行試管嬰兒（IVF）配合胚胎植入前遺傳學檢測（PGT）\n• 使用捐贈的卵子、精子或胚胎\n• 懷孕期間進行產前診斷性檢測\n• 收養\n\n我們會在支持性、非指導性的環境中討論所有可選擇的方案。",
            },
            faq: {
              items: [
                {
                  q: "什麼是攜帶者？",
                  a:
                    "攜帶者通常是指某個基因的一個拷貝帶有致病變異，而另一個拷貝正常運作的人。常染色體隱性遺傳病的攜帶者通常健康。對於大多數這類疾病，雙方伴侶都必須攜帶與同一種疾病相關的致病變異，生育患病孩子的可能性才會增加。",
                },
                {
                  q: "攜帶者常見嗎？",
                  a:
                    "攜帶者很常見，而且多數攜帶者沒有已知家族史。發現攜帶者結果的比例取決於檢測組合包含的基因和疾病、實驗室及受檢人群。對於一種涵蓋 500 多種疾病的擴展性檢測組合，實驗室報告約 70% 的受檢者至少有一項攜帶者結果。",
                },
                {
                  q: "如果我是攜帶者，我的伴侶也需要檢測嗎？",
                  a:
                    "對於大多數常染色體隱性遺傳病，雙方伴侶都需要攜帶與同一種疾病相關的致病變異，生育患病孩子的可能性才會增加。因此，如果發現您是攜帶者，檢測另一位生育伴侶通常是下一步。X 連鎖疾病採用不同的遺傳方式，因此伴侶檢測不一定能回答相同的問題。",
                },
                {
                  q: "如果我是攜帶者，但我的伴侶不是，會怎樣？",
                  a:
                    "對於常染色體隱性遺傳病，如果只有一方生育伴侶攜帶致病變異，生育患該疾病孩子的可能性不會增加。每次懷孕有 50% 的可能性生育同樣為攜帶者的孩子，以及 50% 的可能性生育未遺傳到任何一個家族變異的孩子。這是完成攜帶者篩檢的伴侶中最常見的結果。攜帶者通常健康；如果孩子是攜帶者，將來計劃自己的家庭時可考慮接受篩檢。此外，陰性結果仍然只是降低——而非完全排除——身為攜帶者的可能性。",
                },
                {
                  q: "攜帶者篩檢能檢測所有遺傳疾病嗎？",
                  a:
                    "不能。攜帶者篩檢不包括所有遺傳性疾病，也不能檢出受檢基因中的所有致病變異。陰性結果會降低——但不能排除——您是攜帶者的可能性。剩餘的可能性稱為殘餘風險，並會因疾病、實驗室方法以及個人或家族史而異。",
                },
                {
                  q: "應該在懷孕前還是懷孕期間做攜帶者篩檢？",
                  a:
                    "攜帶者篩檢可在懷孕前或懷孕期間進行。在懷孕前做可以讓您在發現風險增加時擁有最廣泛的生育選擇。不過，在懷孕期間做仍然有價值——它可以幫助您做好準備並指導產前檢測的決定。",
                },
                {
                  q: "如果我和伴侶都是同一疾病的攜帶者，我有什麼選擇？",
                  a:
                    "對於常染色體隱性遺傳病，如果雙方伴侶都攜帶與同一種疾病相關的致病變異，每次懷孕通常有 25% 的可能性生育患病孩子。可選方案可能包括繼續家庭計劃、在適當情況下進行試管嬰兒配合胚胎植入前遺傳學檢測（PGT）、使用捐贈卵子或精子、懷孕期間進行產前診斷性檢測，或收養。我們會在支持性、非指導性的環境中討論所有可選擇的方案。",
                },
              ],
            },
          },
          prenatalScreening: {
            title: "產前篩檢",
            body:
              "產前篩檢其實是好幾種不同的檢測，您能獲得哪幾種，取決於您所在的地區。我們會說明每一種檢測查看什麼、能告訴您什麼、不能告訴您什麼，以及在您所在地哪些項目由公費承擔，協助您決定做哪些、不做哪些。",
            details:
              "產前篩檢用於評估懷孕存在某些染色體疾病的可能性。攜帶者篩檢尋找生育伴侶基因中的變異，而產前篩檢關注懷孕本身。篩檢的內容取決於所選項目：有些查看染色體，有一項還篩檢開放性神經管缺陷，超聲波檢查則關注懷孕的發育情況。\n\n哪些項目可以選擇、哪些由公費承擔，會因省份和國家而異。下面列出的是通常會提供的項目，而不是完整清單：當地的醫療項目可能有不同的組合方式，或使用自己的名稱。我們會說明您所在地區可獲得哪些項目、每一項能檢出和不能檢出什麼，以及您有權選擇不做其中任何一項。\n\n篩檢與診斷性檢測\n篩檢用於估計可能性，不能確認是否存在疾病。絨毛膜取樣（CVS）或羊膜穿刺術等診斷性檢測，可以確定所檢測的特定染色體疾病是否存在。我們會說明每個選項的益處、局限和可能風險，包括選擇不檢測。\n\n解讀您的結果\n高可能性或非典型篩檢結果並不能確認懷孕存在疾病。我們會解釋結果、結合具體情況估算陽性預測值（PPV），並討論遺傳諮詢、詳細超聲波檢查以及透過 CVS 或羊膜穿刺術進行診斷性檢測。低可能性結果會降低所篩檢疾病的可能性，但不能將其降至零。",
            diagram: {
              title: "NIPT 究竟在檢測什麼",
              intro:
                "NIPT 只需採集孕婦的一份血液樣本。樣本中含有許多游離 DNA 片段，而這些片段並非都來自同一處。",
              legend: {
                maternal: "來自孕婦的游離 DNA",
                placental: "來自胎盤的游離 DNA",
              },
              sampleLabel: "一份血液樣本",
              scaleNote: "圖示比例僅供示意——胎盤來源的比例因人而異。",
              notesArray: [
                "樣本中大部分游離 DNA 來自孕婦本人，而非懷孕本身。",
                "只有一小部分來自胎盤。篩檢正是依靠這部分 DNA，因此 NIPT 反映的是胎盤情況，而非直接反映胎兒。",
                "當來自胎盤的比例過低時，實驗室可能無法得出結果。這種情況約佔 1–3%，體重指數較高時更為常見。",
              ],
              footnote:
                "由於 NIPT 檢測的是胎盤的 DNA，結果偶爾可能與胎兒自身的染色體不一致。這也是可能性增加的結果需要透過診斷性檢測確認的原因之一。",
              description:
                "一幅血液樣本圖示，樣本中含有許多游離 DNA 短片段。大部分片段來自孕婦；只有分散其中的一小部分來自胎盤。NIPT 依靠的正是這一小部分來自胎盤的 DNA，因此它反映的是胎盤情況而非直接反映胎兒；當來自胎盤的比例過低時（約佔 1–3%，體重指數較高時更為常見），實驗室可能無法得出結果。",
            },
            screening: {
              nipt: {
                heading: "非侵入性產前篩檢（NIPT）",
                body:
                  "NIPT 又稱游離 DNA 篩檢，分析孕婦血液中循環的游離 DNA 片段。這些片段大部分來自孕婦本人，只有一小部分來自胎盤；檢測正是利用這部分來自胎盤的 DNA 來評估染色體疾病的可能性。它是篩檢常見三體綜合症最敏感、特異性最高的方法，但仍可能出現假陽性、假陰性或無結果。NIPT 不能篩檢所有染色體或結構異常，因此超聲波檢查及其他建議的產前護理仍然重要。\n\nNIPT 通常篩檢什麼\n• 唐氏綜合症（21-三體）\n• 愛德華氏綜合症（18-三體）\n• 帕陶氏綜合症（13-三體）\n• 三倍體（每條染色體都多出一個拷貝），部分檢測組合包含\n• 經選擇作為可選附加項目的性染色體異常\n\n部分實驗室也提供特定微缺失綜合症篩檢。這些疾病很罕見，高可能性結果有較高機會屬於假陽性，因此不建議對一般孕婦常規進行微缺失篩檢。考慮此選項的人應接受檢測前諮詢，並了解必須透過診斷性檢測確認結果。\n\nNIPT 是一種血液篩檢，通常可從懷孕 10 週起進行。對於 21-三體，部分經過驗證的檢測報告檢出率超過 99%、假陽性率低於 0.1%；實際表現會因疾病、實驗室和臨床情況而異。實驗室收到樣本後，結果通常可在 5–10 天內提供，具體時間視實驗室而定。\n\n約 1–3% 的情況下，實驗室無法得出結果，最常見的原因是樣本中來自胎盤的 DNA 過少；體重指數較高時更容易出現這種情況。通常會安排重新抽血，部分實驗室也可選擇退款。無結果本身並不屬於異常結果，但在決定後續步驟前值得先作討論。",
                diagram: {
                  footnote: "由於 NIPT 檢測的是胎盤的 DNA，結果偶爾可能與胎兒自身的染色體不一致。這也是可能性增加的結果需要透過診斷性檢測確認的原因之一。",
                  description:
                    "一幅血液樣本圖示，樣本中含有許多游離 DNA 短片段。大部分片段來自孕婦；只有分散其中的一小部分來自胎盤。NIPT 依靠的正是這一小部分來自胎盤的 DNA，因此它反映的是胎盤情況而非直接反映胎兒；當來自胎盤的比例過低時（約佔 1–3%，體重指數較高時更為常見），實驗室可能無法得出結果。",
                },
              },
              fts: {
                heading: "早孕期篩檢（FTS）",
                body:
                  "早孕期篩檢將胎兒頸部後方的一項超聲波測量（稱為頸項透明層）與孕婦血液中的標記結合起來。檢查通常在懷孕約 11 至 14 週之間進行，用於評估唐氏綜合症和愛德華氏綜合症的可能性。與 NIPT 相比，它能識別的受累懷孕較少，假陽性結果也較多；但它普及程度高，在許多地區可獲得公費資助，而 NIPT 未必如此。",
              },
              serum: {
                heading: "母血清篩檢",
                body:
                  "血清篩檢檢測孕婦血液中的蛋白質和激素，通常在懷孕約 15 至 20 週之間進行。它評估唐氏綜合症和愛德華氏綜合症的可能性；與 NIPT 不同，它還能篩檢脊柱裂等開放性神經管缺陷。部分項目會把早孕期和中孕期兩次採血合併為一個結果，其表現優於單獨任何一次。",
              },
              ultrasound: {
                heading: "超聲波檢查",
                body:
                  "超聲波檢查關注的是懷孕的發育情況，而非染色體。早期核對孕週的掃描可確認懷孕的週數；頸項透明層測量在約 11 至 14 週之間進行；詳細的胎兒結構掃描通常在約 18 至 22 週之間提供。超聲波可以發現任何血液檢測都無法檢出的結構差異，也可以發現改變染色體疾病可能性的特徵。但僅憑超聲波無法確認或排除某種遺傳性疾病。",
              },
            },
            faq: {
              items: [
                {
                  q: "篩檢和診斷性檢測有什麼區別？",
                  a:
                    "篩檢用於估計某種疾病的可能性，不能確認疾病是否存在。透過 CVS 或羊膜穿刺術進行診斷性檢測，可以確定所檢測的特定疾病是否存在。這些操作伴有較小的操作相關流產風險，風險會因操作和醫療中心而異；您的產前護理醫生應向您說明適用於您的估計值。",
                },
                {
                  q: "NIPT 準確嗎？",
                  a:
                    "NIPT 是篩檢常見三體綜合症最敏感、特異性最高的方法。對於 21-三體，部分經過驗證的檢測報告檢出率超過 99%、假陽性率低於 0.1%，但實際表現會因疾病、實驗室和臨床情況而異。NIPT 不是診斷性檢測，仍可能出現假陽性、假陰性或無結果；可能性增加的結果之後應接受遺傳諮詢，並獲得診斷性檢測選項。",
                },
                {
                  q: "什麼時候可以做產前篩檢？",
                  a:
                    "取決於您選擇哪一項。NIPT 通常可從懷孕 10 週起進行，實驗室收到樣本後，結果通常可在 5–10 天內提供。早孕期篩檢在約 11 至 14 週之間進行，因為它需要結合血液檢測和只能在這一時間窗內測量的頸項透明層。母血清篩檢通常在約 15 至 20 週之間進行。超聲波檢查則貫穿其中：早期有核對孕週的掃描，頸項透明層測量在約 11 至 14 週之間，詳細的胎兒結構掃描通常在約 18 至 22 週之間提供。診斷性檢測有自己的時間窗：絨毛膜取樣通常在約 11 至 14 週之間提供，羊膜穿刺術則從約 15 週起。一旦錯過某個時間窗，部分選項就會關閉，因此與其自己猜測，不如盡早詢問。",
                },
                {
                  q: "如果 NIPT 得不出結果怎麼辦？",
                  a:
                    "約 1–3% 的情況下，實驗室無法得出結果，最常見的原因是樣本中來自胎盤的 DNA 過少；體重指數較高時更容易出現這種情況。通常會安排重新抽血，部分實驗室也可選擇退款。無結果本身並不屬於異常結果，但建議與遺傳諮詢師或產前護理醫生討論，由他們評估重新抽血、超聲波檢查，或改用其他篩檢或診斷性檢測是否更適合您的情況。",
                },
                {
                  q: "NIPT 能檢測所有問題嗎？",
                  a:
                    "不能。NIPT 主要篩檢 21-三體、18-三體和 13-三體。性染色體篩檢可在接受適當諮詢後作為自選項目。部分實驗室提供特定微缺失篩檢，但由於這些罕見疾病的檢測表現有限，不建議對一般孕婦常規進行微缺失篩檢。NIPT 不能評估所有染色體疾病、單基因疾病或結構異常，因此建議的超聲波檢查和產前護理仍然重要。",
                },
                {
                  q: "如果收到高風險結果怎麼辦？",
                  a:
                    "可能性增加的結果並不能確認懷孕存在疾病。我們會解釋結果及其局限，結合具體情況估算陽性預測值（PPV），並討論遺傳諮詢、詳細超聲波檢查以及透過 CVS 或羊膜穿刺術進行診斷性檢測。是否進行進一步檢測由您決定。",
                },
                {
                  q: "我在試管嬰兒期間做了 PGT-A，還需要產前篩檢嗎？",
                  a:
                    "PGT-A 會降低但不能排除染色體異常的可能性，也無法識別所有遺傳性或結構性疾病。完成 PGT-A 後，仍應討論產前篩檢和診斷性檢測。最合適的選項取決於您的具體情況、目標和偏好，建議的產前超聲波檢查仍然重要。",
                },
              ],
            },
          },
          hiddenPrenatal: {
            title: "產前篩檢",
            body:
              "所有懷孕人士——不論年齡或初始可能性——都應獲得關於產前篩檢和診斷性檢測的清晰資訊，包括選擇不檢測的權利。GeneFlou 會解釋您所在地區可選擇的方案，並根據您的價值觀、情況和偏好支援共同決策。",
            details:
              "產前篩檢用於評估懷孕存在某些染色體疾病的可能性。攜帶者篩檢尋找生育伴侶基因中的變異，而產前篩檢關注懷孕本身。常見篩檢疾病包括唐氏綜合症（21-三體）、愛德華氏綜合症（18-三體）和帕陶氏綜合症（13-三體）。\n\n篩檢與診斷性檢測\n篩檢用於估計可能性，不能確認是否存在疾病。絨毛膜取樣（CVS）或羊膜穿刺術等診斷性檢測，可以確定所檢測的特定染色體疾病是否存在。我們會說明每個選項的益處、局限和可能風險，包括選擇不檢測。\n\n非侵入性產前篩檢（NIPT）\nNIPT 又稱游離 DNA 篩檢，分析孕婦血液中循環的游離 DNA 片段。這些片段大部分來自孕婦本人，只有一小部分來自胎盤；檢測正是利用這部分來自胎盤的 DNA 來評估染色體疾病的可能性。它是篩檢常見三體綜合症最敏感、特異性最高的方法，但仍可能出現假陽性、假陰性或無結果。NIPT 不能篩檢所有染色體或結構異常，因此超聲波檢查及其他建議的產前護理仍然重要。\n\n解讀您的結果\n高可能性或非典型篩檢結果並不能確認懷孕存在疾病。我們會解釋結果、結合具體情況估算陽性預測值（PPV），並討論遺傳諮詢、詳細超聲波檢查以及透過 CVS 或羊膜穿刺術進行診斷性檢測。低可能性結果會降低所篩檢疾病的可能性，但不能將其降至零。",
            diagram: {
              title: "NIPT 究竟在檢測什麼",
              intro:
                "NIPT 只需採集孕婦的一份血液樣本。樣本中含有許多游離 DNA 片段，而這些片段並非都來自同一處。",
              legend: {
                maternal: "來自孕婦的游離 DNA",
                placental: "來自胎盤的游離 DNA",
              },
              sampleLabel: "一份血液樣本",
              scaleNote: "圖示比例僅供示意——胎盤來源的比例因人而異。",
              notesArray: [
                "樣本中大部分游離 DNA 來自孕婦本人，而非懷孕本身。",
                "只有一小部分來自胎盤。篩檢正是依靠這部分 DNA，因此 NIPT 反映的是胎盤情況，而非直接反映胎兒。",
                "當來自胎盤的比例過低時，實驗室可能無法得出結果。這種情況約佔 1–3%，體重指數較高時更為常見。",
              ],
              footnote:
                "由於 NIPT 檢測的是胎盤的 DNA，結果偶爾可能與胎兒自身的染色體不一致。這也是可能性增加的結果需要透過診斷性檢測確認的原因之一。",
              description:
                "一幅血液樣本圖示，樣本中含有許多游離 DNA 短片段。大部分片段來自孕婦；只有分散其中的一小部分來自胎盤。NIPT 依靠的正是這一小部分來自胎盤的 DNA，因此它反映的是胎盤情況而非直接反映胎兒；當來自胎盤的比例過低時（約佔 1–3%，體重指數較高時更為常見），實驗室可能無法得出結果。",
            },
            whatToExpect: {
              body:
                "產前篩檢用於評估懷孕存在某些染色體疾病的可能性。\n\nNIPT 通常篩檢什麼\n• 唐氏綜合症（21-三體）\n• 愛德華氏綜合症（18-三體）\n• 帕陶氏綜合症（13-三體）\n• 三倍體（每條染色體都多出一個拷貝），部分檢測組合包含\n• 經選擇作為可選附加項目的性染色體異常\n\n部分實驗室也提供特定微缺失綜合症篩檢。這些疾病很罕見，高可能性結果有較高機會屬於假陽性，因此不建議對一般孕婦常規進行微缺失篩檢。考慮此選項的人應接受檢測前諮詢，並了解必須透過診斷性檢測確認結果。\n\n非侵入性產前篩檢（NIPT）\nNIPT 是一種血液篩檢，通常可從懷孕 10 週起進行。對於 21-三體，部分經過驗證的檢測報告檢出率超過 99%、假陽性率低於 0.1%；實際表現會因疾病、實驗室和臨床情況而異。實驗室收到樣本後，結果通常可在 5–10 天內提供，具體時間視實驗室而定。\n\n約 1–3% 的情況下，實驗室無法得出結果，最常見的原因是樣本中來自胎盤的 DNA 過少；體重指數較高時更容易出現這種情況。通常會安排重新抽血，部分實驗室也可選擇退款。無結果本身並不屬於異常結果，但在決定後續步驟前值得先作討論。\n\n篩檢與診斷性檢測\n篩檢用於估計可能性，不能提供診斷。如果結果顯示可能性增加，應討論遺傳諮詢、詳細超聲波檢查以及透過 CVS 或羊膜穿刺術進行診斷性檢測。",
            },
            process: {
              body:
                "第1步：篩檢前諮詢\n遺傳諮詢師會解釋您所在地區可選擇的產前篩檢和診斷性檢測、每項檢測能檢出和不能檢出的內容，以及選擇不檢測的權利。\n\n第2步：選擇方案\n根據當地可及性和您的偏好，選項可能包括 NIPT（游離 DNA 篩檢）、早孕期篩檢（FTS）、其他母體血清篩檢或診斷性檢測。性染色體篩檢屬於需要檢測前諮詢的自選項目。不建議對一般孕婦常規進行微缺失篩檢。\n\n第3步：樣本採集\nNIPT 使用血液樣本，通常可從懷孕 10 週起進行。FTS 結合血液檢測和超聲波檢查，一般在 11–14 週進行。時間和流程取決於所選檢測及當地醫療系統。\n\n第4步：結果與解讀\n實驗室收到樣本後，NIPT 結果通常可在 5–10 天內提供；FTS 的時間則因項目而異。低可能性結果會降低但不能排除所篩檢疾病的可能性。對於高可能性、非典型或無結果的情況，我們會解釋局限，並在適用時估算 PPV 和討論適當的後續步驟。\n\n第5步：診斷性檢測（如適用）\n篩檢結果顯示可能性增加或無法得出結論後，可能會提供 CVS 或羊膜穿刺術等診斷性檢測。我們會解釋每項操作可以檢測什麼、適用時間、局限及較小的操作相關風險，協助您作出知情選擇。",
            },
            faq: {
              items: [
                {
                  q: "篩檢和診斷性檢測有什麼區別？",
                  a:
                    "篩檢用於估計某種疾病的可能性，不能確認疾病是否存在。透過 CVS 或羊膜穿刺術進行診斷性檢測，可以確定所檢測的特定疾病是否存在。這些操作伴有較小的操作相關流產風險，風險會因操作和醫療中心而異；您的產前護理醫生應向您說明適用於您的估計值。",
                },
                {
                  q: "NIPT 準確嗎？",
                  a:
                    "NIPT 是篩檢常見三體綜合症最敏感、特異性最高的方法。對於 21-三體，部分經過驗證的檢測報告檢出率超過 99%、假陽性率低於 0.1%，但實際表現會因疾病、實驗室和臨床情況而異。NIPT 不是診斷性檢測，仍可能出現假陽性、假陰性或無結果；可能性增加的結果之後應接受遺傳諮詢，並獲得診斷性檢測選項。",
                },
                {
                  q: "什麼時候可以做 NIPT？",
                  a:
                    "NIPT 通常可從懷孕 10 週起進行，需要採集孕婦的血液樣本。實驗室收到樣本後，結果通常可在 5–10 天內提供，但具體時間會有所不同。",
                },
                {
                  q: "如果 NIPT 得不出結果怎麼辦？",
                  a:
                    "約 1–3% 的情況下，實驗室無法得出結果，最常見的原因是樣本中來自胎盤的 DNA 過少；體重指數較高時更容易出現這種情況。通常會安排重新抽血，部分實驗室也可選擇退款。無結果本身並不屬於異常結果，但建議與遺傳諮詢師或產前護理醫生討論，由他們評估重新抽血、超聲波檢查，或改用其他篩檢或診斷性檢測是否更適合您的情況。",
                },
                {
                  q: "NIPT 能檢測所有問題嗎？",
                  a:
                    "不能。NIPT 主要篩檢 21-三體、18-三體和 13-三體。性染色體篩檢可在接受適當諮詢後作為自選項目。部分實驗室提供特定微缺失篩檢，但由於這些罕見疾病的檢測表現有限，不建議對一般孕婦常規進行微缺失篩檢。NIPT 不能評估所有染色體疾病、單基因疾病或結構異常，因此建議的超聲波檢查和產前護理仍然重要。",
                },
                {
                  q: "如果收到高風險結果怎麼辦？",
                  a:
                    "可能性增加的結果並不能確認懷孕存在疾病。我們會解釋結果及其局限，結合具體情況估算陽性預測值（PPV），並討論遺傳諮詢、詳細超聲波檢查以及透過 CVS 或羊膜穿刺術進行診斷性檢測。是否進行進一步檢測由您決定。",
                },
                {
                  q: "我在試管嬰兒期間做了 PGT-A，還需要產前篩檢嗎？",
                  a:
                    "PGT-A 會降低但不能排除染色體異常的可能性，也無法識別所有遺傳性或結構性疾病。完成 PGT-A 後，仍應討論產前篩檢和診斷性檢測。最合適的選項取決於您的具體情況、目標和偏好，建議的產前超聲波檢查仍然重要。",
                },
              ],
            },
          },
          learnMore: "了解更多 →",
        },
      },
      team: {
        title: "GeneFlou 團隊",
        olive: {
          name: "Olive Zhu",
          role: "聯合創辦人及遺傳諮詢師",
          bio:
            "Olive 是加拿大認證的遺傳諮詢師，亦是英屬哥倫比亞大學醫學遺傳學系的臨床講師。她的專業領域包括備孕基因檢測及產前遺傳學，曾於加拿大、英國、美國及中國等地工作，對亞洲地區遺傳諮詢服務的發展特別關注。",
        },
        chris: {
          name: "Christopher Chan",
          role: "聯合創辦人",
          bio:
            "Chris 與 Olive 共同創立 GeneFlou，負責機構的網站、日常營運、對外推廣與發展。作為華人，他深知當家庭能以母語交流時，這類對話會順暢得多；他對 GeneFlou 的期望，是讓周到而高質素的遺傳諮詢服務在粵語和普通話中，與英語一樣容易取得。",
        },
      },
      contact: {
        title: "保持聯絡",
        description:
          "歡迎直接電郵至 genetics@geneflou.com，或填寫下方表格與我們聯絡！",
        form: {
          nameLabel: "姓名",
          namePlaceholder: "姓名",
          emailLabel: "電子郵件",
          emailPlaceholder: "電子郵件",
          messageLabel: "留言",
          messagePlaceholder: "請在此輸入您的留言…",
          submit: "傳送訊息",
        },
      },
      language: {
        label: "語言",
        selector: "選擇語言",
        english: "English",
        simplified: "简体中文",
        traditional: "繁體中文",
        shortEnglish: "EN",
        shortSimplified: "简体",
        shortTraditional: "繁體",
      },
      gate: {
        title: "此頁面尚未公開",
        prompt: "此頁面仍在製作中。請輸入密碼繼續，或返回主網站。",
        label: "密碼",
        submit: "繼續",
        error: "密碼不正確，請重試。",
      },
      nav: {
        home: "主頁",
        services: "服務",
        resources: "資源",
        backToTop: "返回頂部",
      },
      resources: {
        hero: {
          title: "資源",
          lede: "當問題超出一次諮詢所能涵蓋的範圍時，我們會推薦這些資源——如何尋找遺傳諮詢師、卑詩省提供哪些服務，以及可以在哪裡進一步了解。",
        },
        opensInNewTab: "在新分頁中開啟",
        group: {
          findCounsellor: {
            title: "尋找遺傳諮詢師",
            entry: {
              cbgc: "查詢加拿大註冊遺傳諮詢師名錄。CBGC 是加拿大遺傳諮詢師的認證機構。",
              nsgc: "收錄加拿大與美國超過 3,300 位遺傳諮詢師，可選擇面診或遠距諮詢。這是一份名錄，並非轉介服務。",
              bcWomens:
                "為卑詩省及育空地區（溫哥華島除外）提供服務的省級遺傳學門診。就診需由醫療服務提供者轉介。",
            },
          },
          prenatalBc: {
            title: "卑詩省的產前篩檢",
            entry: {
              perinatalServicesBc:
                "介紹本省提供哪些產前基因篩檢，以及如何在各項選擇之間作出決定。持有 MSP 保險者免費，並且始終由您自行決定是否接受。",
              gecKoPrenatalGuide:
                "以淺白的語言說明產前篩檢檢測哪些情況，以及一份結果代表什麼、不代表什麼。",
              gecKoNipt:
                "介紹非侵入性產前篩檢的原理、可以篩檢哪些情況，以及為什麼陽性結果需要進一步確診。",
            },
          },
          understandingGenetics: {
            title: "認識遺傳學",
            entry: {
              medlinePlus:
                "美國國家醫學圖書館編寫的指南，涵蓋 1,300 多種疾病，並設有為零基礎讀者而寫的遺傳學入門專欄。",
              gecKoBasics:
                "從基因、染色體和遺傳方式講起，專為沒有生物學基礎的讀者撰寫。",
              gard: "美國國立衛生研究院建立的罕見病與遺傳病資料庫，並有資訊專員直接解答問題。",
              gecKoGnda:
                "說明加拿大法律對誰可以索取您的基因檢測結果的規定，其中包括保險公司與僱主。",
            },
          },
          livingWithCondition: {
            title: "與遺傳疾病同行",
            entry: {
              rareDiseaseFoundation:
                "總部設於溫哥華，提供病友互助與心理健康支援，也包括尚未確診的家庭。",
              cord: "加拿大罕見病組織的全國網絡，可從中找到針對特定疾病的支援團體。",
              unique:
                "提供各類染色體與基因疾病的免費下載指南，並有多種語言版本。機構位於英國，服務世界各地的家庭。",
            },
          },
        },
        disclaimer: {
          title: "關於這些連結",
          body: "這些均為獨立機構。GeneFlou 無法控制其發布的內容，今天準確的頁面日後也可能有所變動。此處連結的任何內容都不能取代針對您自身健康、懷孕或家族史的諮詢——若您讀到的內容引發疑問，歡迎向我們或您的醫療服務提供者提出。",
        },
      },
    },
  },
};

const supportedLanguages = ["en", "zh-Hans", "zh-Hant"];

const getInitialLanguage = () => {
  if (typeof window === "undefined") {
    return "en";
  }

  // `window.localStorage &&` looks like a guard and is the opposite of one:
  // reading the property is itself what raises SecurityError when the origin
  // has no storage access — third-party cookies blocked for the site, an opaque
  // origin. This runs at module load, so throwing here takes i18n's whole
  // initialisation with it and the site renders nothing. A reader in that state
  // simply gets the language their browser asks for, every visit.
  let stored = null;
  try {
    stored = window.localStorage.getItem("geneLanguage");
  } catch (err) {
    // Nothing stored and nowhere to store: fall through to the browser's own
    // language below.
  }
  if (stored && supportedLanguages.includes(stored)) {
    return stored;
  }

  const navLang = (window.navigator.language || "").toLowerCase();

  if (navLang.startsWith("zh")) {
    if (
      navLang.includes("hans") ||
      navLang === "zh-cn" ||
      navLang === "zh-sg"
    ) {
      return "zh-Hans";
    }
    if (
      navLang === "zh-tw" ||
      navLang === "zh-hk" ||
      navLang === "zh-mo" ||
      navLang.includes("hant")
    ) {
      return "zh-Hant";
    }
    // generic zh -> default to Simplified
    return "zh-Hans";
  }

  return "en";
};

const initialLanguage = getInitialLanguage();

document.documentElement.lang = initialLanguage;

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
